/**
 * Mobile-First Customer Decision Portal
 * 
 * Design Principles:
 * - Decision-first, not report-first
 * - Immediate understanding within 5 seconds
 * - Low cognitive load
 * - Clear prioritisation (urgent vs recommended)
 * - Easy authorisation of work
 * - Full transparency on total cost at all times
 * - Customer feels in control
 * - Progressive disclosure for details
 * - No hard blocks - always allow progression
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { formatVehicleName, formatVehicleNameFromObject } from '@/utils/vehicleName';
import axios from 'axios';
import { 
  Check, ChevronDown, ChevronRight, AlertTriangle, AlertCircle, Shield, 
  Camera, Phone, Clock, Car, Zap, CheckCircle2, Circle, XCircle,
  HelpCircle, Wallet, ArrowRight, X, Info, Heart, Activity,
  Droplets, Battery, Disc, MessageCircle, Thermometer, MessageSquareText, ShieldAlert
} from 'lucide-react';
import SATEBlock from '@/components/customer/SATEBlock';
import ShareQuoteButton from '@/components/customer/ShareQuoteButton';
import TyrePortalCard from '@/components/recommendations/TyrePortalCard';
import { RectificationGroupCard } from './RectificationGroupCard';
import { itemApprovalBurst, bulkApprovalBurst } from '@/utils/approvalConfetti';
import { 
  DiagnosticExplanationSection,
  DiagnosticShortWording 
} from '../../components/diagnostic/DiagnosticExplanation';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ============== DECISION CARD COMPONENT ==============
const DecisionCard = ({ 
  item, 
  isUrgent,
  isDangerous,
  isApproved, 
  onApprove, 
  onDecideLater,
  aiExplanation,
  expandedExplanation,
  onToggleExplanation,
  enlargedPhoto,
  onEnlargePhoto,
  // New: Per-item pricing clarity props
  alreadyAuthorisedValue = 0,
  currentSelectedTotal = 0,
  // V2: Intent mode language props
  intentConfig = null,
  jobTypeRules = null
}) => {
  // iter198oo — Photos are now always inline; the showPhotos toggle
  // state has been removed because the tester-evidence row is no
  // longer behind a click.
  
  // V2: Get context phrase from job type rules
  const getContextPhrase = () => {
    if (!jobTypeRules || !item.vi_item_key) return null;
    const jobType = jobTypeRules.vi_item_to_job_type?.[item.vi_item_key];
    if (!jobType) return null;
    const rules = jobTypeRules.job_type_language_rules?.[jobType];
    if (!rules) return null;
    const status = isUrgent ? 'red' : 'amber';
    return rules.context_phrases?.[status] || null;
  };
  
  // V2: Get customer note from job type rules
  const getCustomerNote = () => {
    if (!jobTypeRules || !item.vi_item_key) return null;
    const jobType = jobTypeRules.vi_item_to_job_type?.[item.vi_item_key];
    if (!jobType) return null;
    return jobTypeRules.job_type_language_rules?.[jobType]?.customer_note_suffix || null;
  };
  
  // Get customer-friendly title (outcome-based, not technical)
  const getCustomerTitle = (label) => {
    // Service interval items use their own label directly
    if (item.source_type === "service_interval") {
      return label || 'Service Item';
    }
    
    const titleMappings = {
      'battery': 'Battery Replacement Needed',
      'brake pad': 'Brake Pads Need Replacing',
      'brake fluid': 'Brake Fluid Change Required',
      'tyre': 'Tyre Replacement Needed',
      'oil': 'Oil Change Required',
      'filter': 'Filter Replacement Needed',
      'belt': 'Belt Replacement Required',
      'coolant': 'Coolant Service Required',
      'suspension': 'Suspension Repair Needed',
      'exhaust': 'Exhaust Repair Required',
      'clutch': 'Clutch Repair Needed',
      'steering': 'Steering Repair Required',
      'wiper': 'Wiper Blades Need Replacing'
    };
    
    const lowerLabel = (label || '').toLowerCase();
    for (const [key, title] of Object.entries(titleMappings)) {
      if (lowerLabel.includes(key)) return title;
    }
    return label || 'Repair Needed';
  };
  
  // Get short summary (one line, plain English)
  const getShortSummary = () => {
    // Service interval items use their mapped portal_reason_text
    if (item.source_type === "service_interval" && item.portal_reason_text) {
      return item.portal_reason_text;
    }
    
    if (aiExplanation?.why_it_matters) {
      // Take first sentence only
      const firstSentence = aiExplanation.why_it_matters.split('.')[0];
      return firstSentence.length > 100 ? firstSentence.substring(0, 100) + '...' : firstSentence + '.';
    }
    
    // Fallback based on item notes
    if (item.notes) {
      const shortNotes = item.notes.split('.')[0];
      return shortNotes.length > 100 ? shortNotes.substring(0, 100) + '...' : shortNotes + '.';
    }
    
    return isUrgent 
      ? 'This needs attention now to keep you safe on the road.'
      : 'We recommend addressing this to prevent bigger issues later.';
  };
  
  // Determine card styling based on status
  const getCardStyles = () => {
    if (isApproved) {
      return 'border-green-500 bg-green-50';
    }
    if (isDangerous) {
      return 'border-black bg-slate-900';
    }
    if (isUrgent) {
      return 'border-red-300 bg-red-50';
    }
    return 'border-amber-300 bg-amber-50';
  };
  
  const getTextStyles = () => {
    if (isApproved) return { title: 'text-slate-900', price: 'text-slate-900', summary: 'text-slate-600' };
    if (isDangerous) return { title: 'text-white', price: 'text-white', summary: 'text-slate-300' };
    if (isUrgent) return { title: 'text-red-900', price: 'text-red-900', summary: 'text-red-700' };
    return { title: 'text-amber-900', price: 'text-amber-900', summary: 'text-amber-700' };
  };
  
  const textStyles = getTextStyles();

  return (
    <div 
      className={`rounded-2xl border-4 transition-all overflow-hidden ${getCardStyles()} ${isDangerous && !isApproved ? 'ring-4 ring-red-500/30 shadow-2xl shadow-red-900/50' : ''}`}
      data-testid={`decision-card-${item.id}`}
    >
      {/* Dangerous Badge — prominent safety warning. Larger, animated,
          with multiple visual cues so the customer cannot miss it on
          glance. Per FOH spec: dangerous defects need a more visible
          warning treatment in the customer portal. */}
      {isDangerous && !isApproved && (
        <div
          className="bg-gradient-to-r from-red-600 via-red-700 to-red-600 px-4 py-3 border-b-2 border-yellow-400 relative overflow-hidden"
          data-testid={`dangerous-banner-${item.id}`}
        >
          <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(0,0,0,0.08)_10px,rgba(0,0,0,0.08)_20px)] pointer-events-none" />
          <div className="relative flex items-center gap-3">
            <div className="shrink-0 w-9 h-9 rounded-full bg-yellow-400 flex items-center justify-center animate-pulse ring-2 ring-yellow-200">
              <AlertTriangle className="w-5 h-5 text-red-700" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-yellow-300 leading-tight">
                DVSA Dangerous Defect
              </div>
              <div className="text-sm font-extrabold text-white leading-tight">
                Immediate Safety Risk · Do Not Drive
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Service Interval Badge */}
      {item.source_type === "service_interval" && item.portal_label && !isApproved && (
        <div 
          className={`px-4 py-2 border-b ${
            item.portal_label.startsWith("OVERDUE") 
              ? "bg-red-50 border-red-200" 
              : "bg-blue-50 border-blue-200"
          }`}
          data-testid={`service-badge-${item.id}`}
        >
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" style={{
              color: item.portal_label.startsWith("OVERDUE") ? '#dc2626' : '#2563eb'
            }} />
            <span className={`text-xs font-bold uppercase tracking-wide ${
              item.portal_label.startsWith("OVERDUE") ? "text-red-700" : "text-blue-700"
            }`}>
              {item.portal_label}
            </span>
          </div>
        </div>
      )}
      
      {/* Card Header */}
      <div className="p-4">
        {/* Title + Price Row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <h3 className={`font-semibold text-base leading-tight ${textStyles.title}`}>
              {getCustomerTitle(item.label)}
            </h3>
            {isApproved && (
              <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium mt-1">
                <CheckCircle2 className="w-3 h-3" /> Approved
              </span>
            )}
          </div>
          <div className="text-right">
            <p className={`text-lg font-bold ${textStyles.price}`} data-testid={`item-price-${item.id}`} data-price-pending={item.price_pending ? "true" : "false"}>
              {item.price_pending ? (
                <span className="text-[10.5px] uppercase tracking-wider font-black bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded">
                  Price&nbsp;TBC
                </span>
              ) : (
                <>£{(item.price_inc_vat || 0).toFixed(2)}</>
              )}
            </p>
          </div>
        </div>
        
        {/* Short Summary - Always visible */}
        <p className={`text-sm leading-relaxed mb-2 ${textStyles.summary}`}>
          {getShortSummary()}
        </p>
        
        {/* V2: Job-type context phrase */}
        {getContextPhrase() && (
          <p className={`text-xs leading-relaxed mb-3 italic ${
            isDangerous ? 'text-slate-400' : isUrgent ? 'text-red-600' : 'text-amber-600'
          }`} data-testid={`context-phrase-${item.id}`}>
            {getContextPhrase()}
          </p>
        )}
        
        {/* Diagnostic Short Wording - shown beneath diagnostic line items */}
        {item.is_diagnostic && item.diagnostic_short_wording && (
          <DiagnosticShortWording wording={item.diagnostic_short_wording} />
        )}
        
        {/* Spacer before buttons */}
        <div className="mb-3" />
        
        {/* PROMINENT "Explain this item" Button */}
        <button
          onClick={() => onToggleExplanation(item.id)}
          className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium text-sm mb-3 transition-all ${
            isDangerous && !isApproved
              ? 'bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-600'
              : isApproved
                ? 'bg-green-100 text-green-800 hover:bg-green-200 border border-green-300'
                : isUrgent
                  ? 'bg-red-100 text-red-800 hover:bg-red-200 border border-red-300'
                  : 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300'
          }`}
          data-testid={`explain-${item.id}`}
        >
          <HelpCircle className="w-4 h-4" />
          {expandedExplanation ? 'Hide explanation' : 'Why does this matter?'}
          {expandedExplanation ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        
        {/* Action Buttons - Immediately visible */}
        {!isApproved ? (
          <>
            {/* Per-Item Pricing Message */}
            <p className={`text-xs text-center mb-3 ${
              isDangerous ? 'text-slate-400' : isUrgent ? 'text-red-600' : 'text-amber-700'
            }`}>
              Adding this will bring your total to £{(alreadyAuthorisedValue + currentSelectedTotal + (item.price_inc_vat || 0)).toFixed(2)}
            </p>
            <div className="flex gap-2">
            <button
              onClick={() => onApprove(item.id)}
              className="flex-1 py-3 px-4 rounded-xl font-semibold text-sm transition-all bg-white border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600 shadow-sm"
              data-testid={`approve-${item.id}`}
            >
              Approve repair
            </button>
            <button
              onClick={() => onDecideLater(item.id)}
              className="py-3 px-4 rounded-xl font-medium text-sm border-2 border-red-200 bg-red-50/60 text-red-700 hover:bg-red-100 hover:border-red-300 transition-all"
              data-testid={`decide-later-${item.id}`}
            >
              Decide later
            </button>
          </div>
          </>
        ) : (
          <button
            onClick={() => onDecideLater(item.id)}
            className="w-full py-3 px-4 rounded-xl font-medium text-sm border-2 border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
          >
            Remove from selection
          </button>
        )}
      </div>
      
      {/* iter198oo — Tester Evidence Photos always visible inline.
          Previously hidden behind a "View photos" toggle which cost a
          tap and meant most customers never saw the evidence. Inline
          presentation builds authorisation trust — the customer sees
          exactly what the tester saw (including arrows/circles drawn
          via the PhotoAnnotator at MCC capture time). */}
      {item.photos?.length > 0 && (
        <div className="px-4 pb-4">
          <p className={`text-[10px] font-semibold uppercase tracking-wide mb-1.5 ${
            isDangerous && !isApproved ? 'text-slate-400' : 'text-slate-500'
          }`}>
            <Camera className="w-3 h-3 inline-block mr-1 -mt-0.5" />
            Tester evidence ({item.photos.length})
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`portal-tester-evidence-${item.id}`}>
            {item.photos.map((photo, idx) => (
              <button
                type="button"
                key={idx}
                onClick={(e) => { e.stopPropagation(); onEnlargePhoto(photo, item.photos); }}
                className="relative flex-shrink-0 group focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg"
                aria-label={`Tap to zoom evidence photo ${idx + 1}`}
                data-testid={`portal-evidence-thumb-${item.id}-${idx}`}
              >
                <img
                  src={photo}
                  alt={`Evidence ${idx + 1}`}
                  className="w-20 h-20 object-cover rounded-lg border border-slate-200 group-hover:opacity-90 transition-opacity"
                />
                {/* Zoom hint overlay — appears on hover (desktop) and is
                    always-visible micro on mobile via the bottom-right
                    badge so the customer knows it's tappable. */}
                <span className="absolute bottom-1 right-1 text-[9px] font-bold uppercase tracking-wide bg-slate-900/70 text-white px-1.5 py-0.5 rounded">
                  Tap
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Explanation - Progressive disclosure */}
      {expandedExplanation && (
        <div className={`px-4 pb-4 pt-4 space-y-3 ${
          isDangerous && !isApproved
            ? 'border-t border-slate-700 bg-slate-800'
            : isApproved
              ? 'border-t border-green-200 bg-green-50'
              : isUrgent
                ? 'border-t border-red-200 bg-red-100/50'
                : 'border-t border-amber-200 bg-amber-100/50'
        }`}>
          {(aiExplanation?.what_found || item.explanation?.what_required) && (
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDangerous && !isApproved ? 'text-slate-400' : 'text-slate-500'}`}>What we found</p>
              <p className={`text-sm ${isDangerous && !isApproved ? 'text-slate-200' : 'text-slate-700'}`}>{aiExplanation?.what_found || item.explanation?.what_required}</p>
            </div>
          )}
          {(aiExplanation?.recommendation || item.explanation?.why_required) && (
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDangerous && !isApproved ? 'text-slate-400' : 'text-slate-500'}`}>What we recommend</p>
              <p className={`text-sm ${isDangerous && !isApproved ? 'text-slate-200' : 'text-slate-700'}`}>{aiExplanation?.recommendation || item.explanation?.why_required}</p>
            </div>
          )}
          {(aiExplanation?.why_it_matters || item.explanation?.consequences) && (
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDangerous && !isApproved ? 'text-slate-400' : 'text-slate-500'}`}>Why it matters</p>
              <p className={`text-sm italic ${isDangerous && !isApproved ? 'text-slate-300' : 'text-slate-600'}`}>{aiExplanation?.why_it_matters || item.explanation?.consequences}</p>
            </div>
          )}
          {aiExplanation?.mot_note && (
            <div className={`flex items-start gap-2 p-2.5 rounded-lg ${isDangerous && !isApproved ? 'bg-slate-700' : 'bg-blue-50'}`}>
              <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDangerous && !isApproved ? 'text-blue-400' : 'text-blue-600'}`} />
              <p className={`text-xs ${isDangerous && !isApproved ? 'text-blue-300' : 'text-blue-800'}`}><strong>MOT:</strong> {aiExplanation.mot_note}</p>
            </div>
          )}

          {/* Customer-facing PAIR RECOMMENDATION — surfaces the
              "consider replacing both sides" advisory the FOH sees on
              the pricing screen. Only fires for symmetric components
              (suspension arms, wishbones) where one side is priced and
              the other isn't on the yestimate. Backend-driven so the
              copy stays consistent with FOH guidance.

              NOTE — Phase-5 `subject_to_inspection` card was historically
              rendered here, but this `expandedExplanation` branch is no
              longer in the active render tree. The live render path is
              the "WHAT THIS MEANS / WHAT'S REQUIRED / WHAT HAPPENS IF NOT
              ADDRESSED" block below — see lines ~1379 + ~1692 where the
              card actually hangs off the live items. */}
          {item.pair_recommendation && (
            <div
              className={`flex items-start gap-2 p-3 rounded-lg border ${
                isDangerous && !isApproved
                  ? "bg-slate-700 border-slate-600"
                  : "bg-amber-50 border-amber-300"
              }`}
              data-testid={`portal-pair-recommendation-${item.id}`}
            >
              <AlertCircle
                className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                  isDangerous && !isApproved ? "text-amber-300" : "text-amber-700"
                }`}
              />
              <div className="min-w-0">
                <p
                  className={`text-[12.5px] font-bold uppercase tracking-[0.14em] mb-1 ${
                    isDangerous && !isApproved ? "text-amber-200" : "text-amber-900"
                  }`}
                >
                  {item.pair_recommendation.headline}
                </p>
                <p
                  className={`text-[13px] leading-snug ${
                    isDangerous && !isApproved ? "text-amber-100" : "text-amber-900/90"
                  }`}
                >
                  {item.pair_recommendation.message}
                </p>
              </div>
            </div>
          )}

          {/* Customer-facing advisory notes (auto-derived from JG content).
              Brake-pad → disc-replacement-may-be-required.
              Bulb → further-investigation-may-be-required.
              FOH may have toggled individual notes off in the pricing
              UI; the backend only returns ``enabled`` notes here. */}
          {(item.customer_notes || []).map((note) => (
            <div
              key={note.id || note.key}
              className={`flex items-start gap-2 p-2.5 rounded-lg border ${
                isDangerous && !isApproved
                  ? 'bg-slate-700 border-slate-600'
                  : 'bg-amber-50 border-amber-200'
              }`}
              data-testid={`portal-customer-note-${note.key}`}
            >
              <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                isDangerous && !isApproved ? 'text-amber-400' : 'text-amber-600'
              }`} />
              <p className={`text-xs leading-relaxed ${
                isDangerous && !isApproved ? 'text-amber-200' : 'text-amber-900'
              }`}>
                <strong>Please note: </strong>{note.text}
              </p>
            </div>
          ))}

          {/* SATE — Layer 2 workflow/process explanation. Sits BETWEEN
              the deterministic explanation and the Important Note so
              the customer reads: what we found → how the process
              works → workshop nuance. */}
          <SATEBlock sate={item.sate} dark={isDangerous && !isApproved} />

          {/* AI-assisted Contextual Customer Note (CCN). Visually
              SECONDARY to the deterministic explanation — different
              colour, "Advisor Contextual Note" label, subtle separator.
              The objective inspection system and advisor guidance MUST
              stay clearly distinct. Only renders when FOH has enabled
              the note AND there is approved final text. */}
          {/* iter197m — High-visibility "*Important Note" surface. Used
              to be deliberately subtle slate-coloured but FOH feedback
              was customers were missing it. Now amber-bordered with a
              ring, bolder label + body, AlertCircle icon. */}
          {item.contextual_customer_note?.text && (
            <div
              className={`mt-2 flex items-start gap-2.5 p-3 rounded-lg border-2 ring-1 shadow-sm ${
                isDangerous && !isApproved
                  ? 'bg-amber-950/40 border-amber-500/70 ring-amber-500/30'
                  : 'bg-amber-50 border-amber-400 ring-amber-200'
              }`}
              data-testid={`portal-contextual-note-${item.id}`}
            >
              <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                isDangerous && !isApproved ? 'text-amber-300' : 'text-amber-700'
              }`} />
              <div className="min-w-0">
                <p className={`text-xs uppercase tracking-wide font-bold mb-1 ${
                  isDangerous && !isApproved ? 'text-amber-200' : 'text-amber-900'
                }`}>
                  *Important Note
                </p>
                <p className={`text-sm leading-relaxed font-medium ${
                  isDangerous && !isApproved ? 'text-amber-50' : 'text-amber-950'
                }`}>
                  {item.contextual_customer_note.text}
                </p>
              </div>
            </div>
          )}
          
          {/* Pair Replacement Explanation - Why this is recommended as a pair */}
          {item.pair_replacement_info?.customer_explanation && (
            <div className={`flex items-start gap-2 p-2.5 rounded-lg ${isDangerous && !isApproved ? 'bg-slate-700' : 'bg-purple-50'}`}>
              <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDangerous && !isApproved ? 'text-purple-400' : 'text-purple-600'}`} />
              <div>
                <p className={`text-xs font-semibold mb-1 ${isDangerous && !isApproved ? 'text-purple-300' : 'text-purple-800'}`}>
                  Why both sides?
                </p>
                <p className={`text-xs ${isDangerous && !isApproved ? 'text-purple-300' : 'text-purple-700'}`}>
                  {item.pair_replacement_info.customer_explanation}
                </p>
              </div>
            </div>
          )}
          
          {/* V2: Job-type customer note */}
          {getCustomerNote() && (
            <p className={`text-xs mt-2 pt-2 border-t ${
              isDangerous && !isApproved
                ? 'border-slate-600 text-slate-400'
                : isUrgent
                  ? 'border-red-200 text-slate-500'
                  : 'border-amber-200 text-slate-500'
            }`} data-testid={`customer-note-${item.id}`}>
              {getCustomerNote()}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * DangerousAcknowledgeBanner
 * --------------------------
 * Two-layer urgent-safety banner shown above the customer items —
 * iter197nn trust-first redesign.
 *
 *   Layer 1 — Headline:    calm "Dangerous MOT Defect Identified"
 *                          with single subline informing the customer
 *                          the vehicle should not be driven until
 *                          repaired. No legal jargon at this level.
 *   Layer 2 — Learn more:  collapsible expandable carrying the
 *                          legal context (penalty points, fine,
 *                          insurance, vehicle-seizure powers). Hidden
 *                          by default so the customer's first read is
 *                          the mechanical fact, not the consequence
 *                          list. Available on demand.
 *   Ack gate:              tap "I understand — show me the safety
 *                          items" to collapse the banner. Localstorage-
 *                          backed so it stays dismissed for this token
 *                          if the customer returns.
 *
 * iter197b → iter197nn: the prior version led with bright "DVSA
 * Dangerous Defect Identified" + a stage-2 legal block above the
 * inspection findings, which the customer feedback identified as
 * fear-led rather than trust-led. The portal must inform, not
 * frighten — the legal text is preserved but moved one tap behind a
 * "Learn more" link so the customer who needs it can reach it without
 * everyone reading it first.
 */
function DangerousAcknowledgeBanner({ tokenId }) {
  const storageKey = `yestimate.dangerous_ack.${tokenId || "unknown"}`;
  const [acknowledged, setAcknowledged] = useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });

  const handleAck = () => {
    setAcknowledged(true);
    try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
  };

  if (acknowledged) {
    return (
      <div
        className="rounded-lg bg-red-700 text-white shadow px-3 py-2 mb-4 flex items-center gap-2"
        data-testid="portal-dangerous-acknowledged-banner"
      >
        <AlertTriangle className="w-4 h-4 text-yellow-300 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-black uppercase tracking-wider text-yellow-200">
            Dangerous Defect Acknowledged
          </p>
          <p className="text-xs leading-tight">
            Do not drive until repaired. See safety items below.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setAcknowledged(false);
            try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
          }}
          className="text-[10px] underline text-yellow-100/90 hover:text-white shrink-0"
          data-testid="portal-dangerous-reopen"
        >
          Reopen
        </button>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl bg-gradient-to-r from-red-700 via-red-600 to-red-700 ring-2 ring-red-800 shadow-lg p-4 mb-4"
      data-testid="portal-dangerous-top-banner"
    >
      {/* iter197nn — Calmer, trust-first dangerous-defect banner.
          Headline + single subline that informs without frightening.
          All legal / penalty content is moved behind a "Learn more"
          expandable so the customer's first read is the mechanical
          fact, not the consequence list. */}
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center ring-2 ring-yellow-200">
          <AlertTriangle className="w-6 h-6 text-red-800" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 text-white flex-1">
          <p className="text-base font-extrabold leading-tight">
            Dangerous MOT Defect Identified
          </p>
          <p className="text-sm leading-snug mt-1 text-yellow-50">
            This vehicle has been issued with a Dangerous MOT defect and should not be driven until repaired.
          </p>
        </div>
      </div>

      {/* Learn more — collapsible legal context. Hidden by default
          so it doesn't compete with the headline above. Customer
          can open it when (and only when) they want the detail. */}
      <details
        className="mt-3 group"
        data-testid="portal-dangerous-learn-more"
      >
        <summary className="cursor-pointer list-none inline-flex items-center gap-1.5 text-[12px] font-medium text-yellow-100 hover:text-yellow-50 underline underline-offset-2 select-none">
          <span>Learn more about driving restrictions</span>
          <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
        </summary>
        <div
          className="mt-2 rounded-md bg-red-900/40 border border-red-800/60 px-3 py-2 text-white"
          data-testid="portal-dangerous-legal-box"
        >
          <ul className="text-[11.5px] leading-snug space-y-1 list-disc list-inside text-yellow-50">
            <li>
              <strong>3 penalty points</strong> on your licence, and up to
              <strong> £2,500 fine</strong> per defect for using a vehicle
              in a dangerous condition <span className="text-yellow-200/90">(Road Traffic Act 1988, s.40A)</span>.
            </li>
            <li>
              Your <strong>insurance may be invalidated</strong> —
              you would be personally liable for any third-party damage
              or injury.
            </li>
            <li>
              Police can <strong>seize the vehicle</strong> at the roadside
              and issue a fixed-penalty notice.
            </li>
          </ul>
        </div>
      </details>

      {/* Acknowledge gate */}
      <button
        type="button"
        onClick={handleAck}
        className="mt-3 w-full bg-yellow-400 hover:bg-yellow-300 text-red-900 font-extrabold uppercase tracking-wider text-[12px] px-4 py-2.5 rounded-md shadow flex items-center justify-center gap-2"
        data-testid="portal-dangerous-ack-btn"
      >
        I understand — show me the safety items
      </button>
    </div>
  );
}



// ============== MAIN PORTAL COMPONENT ==============
const MobileCustomerPortal = () => {
  const { token } = useParams();
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  // iter211g — Diag+rectification pairs, fetched from
  // /api/portal/{token}/rectification-groups. Rendered as one grouped
  // invoice card at the top of the list; the individual diag & rect
  // items are also suppressed from the main flow so the customer
  // never sees them twice.
  const [rectificationGroups, setRectificationGroups] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Decision state
  const [selectedOption, setSelectedOption] = useState(null); // 'all', 'essential', 'budget'
  const [approvedItems, setApprovedItems] = useState(new Set());
  // Iter189 — per-tyre decline tracking. Shape:
  //   { [tyre_group_jg_id]: ["OSF", "OSR"] }
  // Each TyrePortalCard pushes its current decline set up via
  // onPerTyreDecisionChange so the submit payload carries the full
  // per-child decision tree to the backend.
  const [perTyreDeclines, setPerTyreDeclines] = useState({});
  // iter198s (Feb 2026) — track the inverse: per-tyre group, which
  // positions has the customer EXPLICITLY approved (touched & not
  // declined). Used to: (1) include partial tyre-group prices in the
  // bottom-bar "+ Additional" total, and (2) auto-promote the parent
  // tyre group into approvedItems when the customer approves a tyre
  // individually (without selecting a top-level option first).
  const [perTyreApproved, setPerTyreApproved] = useState({});
  // iter204d — Per-tyre puncture-repair selections.
  //   { [tyre_group_jg_id]: ["NSR"] }
  // Submitted alongside declined/approved positions so FOH sees the
  // customer's preference for the £25 repair-instead-of-replace path.
  const [perTyreRepairs, setPerTyreRepairs] = useState({});
  const handlePerTyreDecisionChange = useCallback((itemId, declinedPositions, approvedPositions = [], repairPositions = []) => {
    setPerTyreDeclines((prev) => {
      // iter198u — Bail when the new value is identical to the
      // previous one. Without this guard, the parent created a fresh
      // object reference on every emission and the child's useEffect
      // re-fired on every parent render, looping until React's
      // bailout finally kicked in. The loop wasn't breaking
      // anything visually but it stole click events from neighbouring
      // tyre buttons before they were registered as new touches.
      const prevList = prev[itemId] || [];
      const same =
        prevList.length === (declinedPositions || []).length
        && prevList.every((p, i) => p === declinedPositions[i]);
      if (same) return prev;
      const next = { ...prev };
      if (!declinedPositions || declinedPositions.length === 0) delete next[itemId];
      else next[itemId] = declinedPositions;
      return next;
    });
    setPerTyreApproved((prev) => {
      const prevList = prev[itemId] || [];
      const same =
        prevList.length === (approvedPositions || []).length
        && prevList.every((p, i) => p === approvedPositions[i]);
      if (same) return prev;
      const next = { ...prev };
      if (!approvedPositions || approvedPositions.length === 0) delete next[itemId];
      else next[itemId] = approvedPositions;
      return next;
    });
    // iter204d — Repair-instead-of-replace selections.
    setPerTyreRepairs((prev) => {
      const prevList = prev[itemId] || [];
      const same =
        prevList.length === (repairPositions || []).length
        && prevList.every((p, i) => p === repairPositions[i]);
      if (same) return prev;
      const next = { ...prev };
      if (!repairPositions || repairPositions.length === 0) delete next[itemId];
      else next[itemId] = repairPositions;
      return next;
    });
    setApprovedItems((prev) => {
      const hasApprovals = approvedPositions && approvedPositions.length > 0;
      const hasDeclines = declinedPositions && declinedPositions.length > 0;
      const alreadyIn = prev.has(itemId);
      // Bail on no-op transitions.
      if (hasApprovals && alreadyIn) return prev;
      if (!hasApprovals && !alreadyIn) return prev;
      const next = new Set(prev);
      if (hasApprovals) {
        next.add(itemId);
      } else if (selectedOption !== 'all') {
        if (hasDeclines || alreadyIn) {
          next.delete(itemId);
        }
      }
      return next;
    });
  }, [selectedOption]);
  const [decideLaterItems, setDecideLaterItems] = useState(new Set());
  const [declinedItems, setDeclinedItems] = useState({}); // { itemId: reason }
  // iter198aa — Track which items have already triggered a confetti
  // burst this session so we celebrate the *moment* of approval once
  // per item (toggling decline→approve→decline doesn't replay).
  const confettiSeenRef = useRef(new Set());
  const bulkConfettiSeenRef = useRef(false);
  // iter198cc — Snapshot of authorised totals after Confirm & Authorise
  // so the "Thank You" screen can show the actual figure the customer
  // committed to. ``null`` while the customer is still deciding.
  const [authorisedSnapshot, setAuthorisedSnapshot] = useState(null);
  const [showDeclineDropdown, setShowDeclineDropdown] = useState({}); // { itemId: true/false }
  // iter199ak (Feb 2026) — Ref to the Confirm section so the sticky
  // bottom CTA can scrollIntoView() when tapped. Without the ref the
  // CTA could expand the section out-of-view on a long quote and the
  // customer would still think nothing happened.
  const confirmSectionRef = useRef(null);
  
  // Budget flow state
  const [showBudgetInput, setShowBudgetInput] = useState(false);
  const [budgetAmount, setBudgetAmount] = useState('');
  const [budgetWarning, setBudgetWarning] = useState(null);
  const [budgetProceedAnyway, setBudgetProceedAnyway] = useState(false);
  
  // UI state
  const [expandedExplanations, setExpandedExplanations] = useState({});
  const [showHealthReport, setShowHealthReport] = useState(false);
  // iter198oo — Photo viewer state. Carries the full photo set for the
  // clicked item + which one is active so the full-screen modal can
  // render a thumbnail nav strip + prev/next arrows. The legacy
  // single-photo shape is preserved for older callers via a tiny
  // setter helper below.
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [enlargedSet, setEnlargedSet] = useState([]);
  /**
   * Open the full-screen photo viewer. Accepts either:
   *   (a) a single photo URL — legacy callers, treated as a 1-photo set
   *   (b) (photoUrl, allPhotos[]) — opens viewer with thumbnail nav
   */
  const openPhotoViewer = (photo, allPhotos) => {
    setEnlargedPhoto(photo);
    setEnlargedSet(Array.isArray(allPhotos) && allPhotos.length > 0 ? allPhotos : [photo]);
  };
  const closePhotoViewer = () => {
    setEnlargedPhoto(null);
    setEnlargedSet([]);
  };
  const [showConfirmSection, setShowConfirmSection] = useState(false);
  
  // Summary expansion state
  const [urgentSummaryExpanded, setUrgentSummaryExpanded] = useState(false);
  const [recommendedSummaryExpanded, setRecommendedSummaryExpanded] = useState(false);
  
  // Individual item expansion state within summary boxes (Level 2 accordion)
  const [expandedSummaryItems, setExpandedSummaryItems] = useState({});
  
  // Customer details
  const [customerName, setCustomerName] = useState('');
  const [typedSignature, setTypedSignature] = useState('');
  
  // AI explanations
  const [aiExplanations, setAiExplanations] = useState({});
  
  // Garage branding
  const [garageLogo, setGarageLogo] = useState(null);
  const [garageName, setGarageName] = useState(null);
  
  // Customer Intent Mode
  const [intentConfig, setIntentConfig] = useState(null);
  
  // V2: Job type language rules
  const [jobTypeRules, setJobTypeRules] = useState(null);
  
  // Signature canvas ref
  const canvasRef = useRef(null);

  // ============== DATA FETCHING ==============
  useEffect(() => {
    fetchData();
    fetchGarageLogo();
    fetchJobTypeRules();
  }, [token]);
  
  useEffect(() => {
    if (data) {
      fetchAiExplanations();
    }
  }, [data]);
  
  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/customer/approval/${token}`);
      setData(res.data);
      if (res.data.customer_name) {
        setCustomerName(res.data.customer_name);
        setTypedSignature(res.data.customer_name);
      }
      if (res.data.already_responded) {
        setSubmitted(true);
      }
      // Load intent configuration if present
      if (res.data.intent_config) {
        setIntentConfig(res.data.intent_config);
      }
      // iter211g — Fetch diag+rectification group pairs so the portal
      // can render them as one grouped invoice-style card at the top
      // of the item list, and suppress the individual entries from
      // the main flow.
      try {
        const rg = await axios.get(`${API}/portal/${token}/rectification-groups`);
        setRectificationGroups(rg.data.groups || []);
      } catch (_) {/* silent — non-critical enrichment */}
    } catch (err) {
      // iter198bb (Feb 2026) — Make the error message specific so FOH
      // can tell network glitches from a genuinely-expired token.
      // Previously every axios failure read "link expired" — misleading
      // when the real cause was a 5xx or a network blip.
      // eslint-disable-next-line no-console
      console.error('[MobileCustomerPortal] fetchData failed', {
        status:   err?.response?.status,
        data:     err?.response?.data,
        message:  err?.message,
      });
      const status = err?.response?.status;
      if (status === 404) {
        setError('This link is invalid or has expired. Please contact the garage for a new link.');
      } else if (status >= 500) {
        setError(`Server error (${status}). Please refresh the page in a moment — if it keeps failing, contact the garage.`);
      } else if (err?.message === 'Network Error' || !err?.response) {
        setError('Connection issue. Please check your internet and tap reload — your link is still valid.');
      } else {
        setError(`Unexpected error${status ? ` (${status})` : ''}. Please contact the garage to resend your link.`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchGarageLogo = async () => {
    try {
      const res = await axios.get(`${API}/public/garage/logo`);
      if (res.data.has_logo && res.data.logo_data) {
        setGarageLogo(res.data.logo_data);
        setGarageName(res.data.garage_name);
      }
    } catch (err) {
      // Logo is optional
    }
  };
  
  const fetchAiExplanations = async () => {
    try {
      const res = await axios.post(`${API}/customer/approval/${token}/generate-explanations`);
      if (res.data.explanations) {
        setAiExplanations(res.data.explanations);
      }
    } catch (err) {
      // AI explanations are optional enhancement
    }
  };
  
  const fetchJobTypeRules = async () => {
    try {
      const res = await axios.get(`${API}/intent-mode/v2/job-type-rules`);
      setJobTypeRules(res.data);
    } catch (err) {
      // V2 rules are optional enhancement
    }
  };

  // ============== CALCULATIONS ==============
  const alreadyAuthorisedValue = data?.agreed_work_value || 0;
  // Global charges (workshop consumables + environmental disposal) are
  // workshop-wide mandatory charges that apply regardless of which items
  // the customer ticks. They are ALWAYS included in the grand total so
  // the portal mirrors the pricing screen exactly (no surprise gap
  // between what FOH sees and what the customer sees).
  const chargesTotalIncVat = Number(data?.charges_total_inc_vat || 0);

  const urgentItems = data?.red_items || [];
  const recommendedItems = data?.amber_items || [];
  const allItems = [...urgentItems, ...recommendedItems];

  // iter211g — When a diag+rectification pair has been folded into a
  // grouped card at the top of the portal, suppress the same items
  // from the flat red/amber lists so the customer sees them exactly
  // once. Backend can emit the diag item id in raw form OR prefixed
  // "diag_<id>" (two different render paths), so we match both.
  const rectSuppressedItemIds = new Set();
  (rectificationGroups || []).forEach((g) => {
    if (g?.rect?.id) rectSuppressedItemIds.add(g.rect.id);
    if (g?.diag?.id) {
      rectSuppressedItemIds.add(g.diag.id);
      rectSuppressedItemIds.add(`diag_${g.diag.id}`);
    }
  });
  const _filterOutRect = (arr) => arr.filter((it) => !rectSuppressedItemIds.has(it?.id));
  const _visibleUrgentItems = _filterOutRect(urgentItems);
  const _visibleRecommendedItems = _filterOutRect(recommendedItems);

  // iter197b — Items where pricing is still pending (e.g. a tyre group
  // where FOH hasn't priced the tyres yet) must NOT contribute their
  // misleading charges-only number to the urgent / recommended rollups.
  // The header instead surfaces "(+ Price TBC)" when any pending item
  // exists in that bucket.
  const _isPricePending = (it) => it?.tyre_group_price_pending === true || it?.price_pending === true;
  const urgentHasPending = urgentItems.some(_isPricePending);
  const recommendedHasPending = recommendedItems.some(_isPricePending);
  const urgentTotal = urgentItems.reduce(
    (sum, item) => sum + (_isPricePending(item) ? 0 : (item.price_inc_vat || 0)),
    0
  );
  const recommendedTotal = recommendedItems.reduce(
    (sum, item) => sum + (_isPricePending(item) ? 0 : (item.price_inc_vat || 0)),
    0
  );
  const allWorkTotal = urgentTotal + recommendedTotal;
  const grandTotal = allWorkTotal + alreadyAuthorisedValue + chargesTotalIncVat;

  // iter198p (Feb 2026) — "Essential safety work only" path. Only items
  // that are CURRENT MOT FAILS (major/dangerous from DVSA) qualify.
  // Advisories, FOH-added tyres, and engine-recommended services
  // (wheel alignment, AC service, etc.) are explicitly excluded — the
  // customer should not be told these are legally required.
  //
  // For tyre groups with mixed severity, the group itself is included
  // but only the essential-safety child tyres count toward the total
  // (the non-essential tyres get auto-declined via per-tyre toggles).
  const _essentialSafetyTyrePriceForGroup = (item) => {
    if (item?.tyre_group_subtype !== "tyre_replacement_group") return null;
    const tyres = item?.tyre_group_tyres || [];
    if (!tyres.length) return null;
    let sum = 0;
    let any = false;
    for (const t of tyres) {
      if (!t?.is_essential_safety) continue;
      if (!t?.replacement_required) continue;
      const p = t?.allocated_price_inc_vat;
      if (typeof p === "number") {
        sum += p;
        any = true;
      } else if (p === null) {
        // TBC tyre in essential set — surface as pending.
        return { total: 0, pending: true };
      }
    }
    return any ? { total: sum, pending: false } : { total: 0, pending: false };
  };

  // iter198ww (Feb 2026) — Widen the "essential safety" set so the
  // third option appears whenever the customer is looking at ANY
  // dangerous / red item, not strictly DVSA MOT FAILs. User
  // feedback: the option vanished on portals where the urgent
  // bucket carried dangerous items (e.g. RIE-classified, or
  // service-recommendation-tagged dangerous) because none of them
  // had `is_essential_safety=true` set server-side. We now treat
  // the union of (`is_essential_safety` ∪ red_items[]) as the
  // essential safety set on the FE — server-side `is_essential_safety`
  // remains the strict legal flag for analytics & decision_type
  // wiring (`approve_safety_only`).
  const essentialSafetyItems = (() => {
    const strictSet = allItems.filter((i) => i?.is_essential_safety === true);
    if (strictSet.length > 0) return strictSet;
    // Fallback — show the option for any urgent (red) item so the
    // customer always has a "do only the dangerous bits" choice on
    // any portal that includes red work.
    return urgentItems;
  })();
  const essentialSafetyTotal = essentialSafetyItems.reduce((sum, item) => {
    if (_isPricePending(item)) return sum;
    const tyreSplit = _essentialSafetyTyrePriceForGroup(item);
    if (tyreSplit) {
      return sum + (tyreSplit.pending ? 0 : tyreSplit.total);
    }
    return sum + (item.price_inc_vat || 0);
  }, 0);

  // For each tyre group in the essential set, compute the list of
  // tyre positions to AUTO-DECLINE when the customer picks "essential
  // safety only" (i.e. positions that are NOT essential safety).
  const _autoDeclinePositionsForEssential = (item) => {
    if (item?.tyre_group_subtype !== "tyre_replacement_group") return [];
    const tyres = item?.tyre_group_tyres || [];
    return tyres
      .filter((t) => t?.replacement_required && !t?.is_essential_safety)
      .map((t) => t?.position)
      .filter(Boolean);
  };

  // Calculate selected work total
  const getSelectedTotal = () => {
    // iter197e — Workshop charges (consumables + environmental) apply
    // ONLY when the customer is actually authorising additional work.
    // If they've selected nothing beyond the already-agreed MOT, the
    // garage isn't doing any chargeable workshop activity, so the
    // £22.20 floor should NOT be on the bill. Previously the math
    // baked them in unconditionally, making "Total if approved" >
    // "Already agreed" + "Additional work selected" by exactly the
    // charges total even when no extras were picked. User-reported
    // on LJ65WLC: £54.85 + £0.00 was rendering as £77.05.
    let workTotal = 0;
    let hasAdditionalWork = false;

    if (selectedOption === 'all') {
      workTotal += allWorkTotal;
      hasAdditionalWork = allWorkTotal > 0;
    } else if (selectedOption === 'essential') {
      workTotal += essentialSafetyTotal;
      hasAdditionalWork = essentialSafetyTotal > 0;
    } else if (selectedOption === 'budget' && budgetAmount) {
      // Budget-based selection — sum the items the wizard actually
      // chose (pair bundling may leave the wizard genuinely under-budget,
      // so we must NOT default to the typed-in budget number).
      const included = getIncludedItems();
      included.forEach((item) => {
        const p = item.price_inc_vat || 0;
        workTotal += p;
        if (p > 0) hasAdditionalWork = true;
      });
    } else {
      // Manual selection
      approvedItems.forEach(itemId => {
        const item = allItems.find(i => i.id === itemId);
        if (item) {
          let p = item.price_inc_vat || 0;
          // iter198s — For tyre groups with per-tyre approvals, sum
          // only the APPROVED tyres' allocated prices rather than the
          // whole group price.
          // iter198t — Use the per-tyre sum WHENEVER the customer has
          // touched the group (perTyreApproved OR perTyreDeclines has
          // an entry). Empty approved set with any declines → £0,
          // NOT the fallback whole-group price. Previously the
          // falsy-perTyreApproved check fell through and a customer
          // who approved-then-declined every tyre still saw the full
          // £359.76 in "+ Additional".
          if (item.tyre_group_subtype === 'tyre_replacement_group') {
            const hasPerTyreTouch = (
              (perTyreApproved[itemId] && perTyreApproved[itemId].length > 0)
              || (perTyreDeclines[itemId] && perTyreDeclines[itemId].length > 0)
            );
            if (hasPerTyreTouch) {
              const approvedPositions = new Set(perTyreApproved[itemId] || []);
              const tyres = item.tyre_group_tyres || [];
              p = tyres
                .filter((t) => t.replacement_required && approvedPositions.has(t.position))
                .reduce((s, t) => s + (t.allocated_price_inc_vat || 0), 0);
            }
          }
          workTotal += p;
          if (p > 0) hasAdditionalWork = true;
        }
      });
    }

    let total = alreadyAuthorisedValue + workTotal;
    if (hasAdditionalWork) {
      total += chargesTotalIncVat;
    }
    return total;
  };
  
  // Get items included in current selection
  const getIncludedItems = () => {
    if (selectedOption === 'all') return allItems;
    if (selectedOption === 'essential') return essentialSafetyItems;
    if (selectedOption === 'budget' && budgetAmount) {
      // Prioritise by urgency, then by price (cheapest first within urgency).
      // Pair-aware: when an item has a `pair_group_key`, we attempt to
      // bundle every sibling on the same family+axis in the same tier so
      // the customer doesn't pay for alignment twice and asymmetric wear
      // is avoided. If the full pair doesn't fit, we skip it entirely
      // (option c — try the next priority that fits, fall back to a
      // single-side warning only when nothing else fits).
      const budget = parseFloat(budgetAmount) || 0;
      let remaining = budget;
      const included = [];
      const includedIds = new Set();

      // Helper: greedy bundle pass within a tier of items.
      //   - allowPair=true   → strict pass. Phase A places EVERY fittable
      //                        full pair (cheapest-bundle first) before
      //                        any un-paired items get a chance — this
      //                        is the user-asked invariant: "if budget
      //                        covers BOTH sides of a pair, bundle
      //                        them". Phase B then fills the remainder
      //                        with un-paired items cheapest-first.
      //   - allowPair=false  → fallback pass that admits single-side
      //                        items even when the pair-mate didn't fit,
      //                        so the customer isn't left with nothing.
      const takeFromTier = (tier, allowPair) => {
        if (allowPair) {
          // --- Phase A: pair bundles first ---
          // Group tier items by pair_group_key, but pull SIBLINGS from
          // ANY tier (a red side may be paired with an amber side once
          // the red side has been promoted — we still bundle).
          const bundlesByKey = new Map();
          for (const item of tier) {
            if (includedIds.has(item.id)) continue;
            const groupKey = item.pair_group_key;
            if (!groupKey || bundlesByKey.has(groupKey)) continue;
            const siblings = allItems.filter(
              (i) => i.pair_group_key === groupKey && !includedIds.has(i.id)
            );
            const bundleCost = siblings.reduce(
              (s, i) => s + (i.price_inc_vat || 0),
              0
            );
            bundlesByKey.set(groupKey, { siblings, bundleCost });
          }
          // Sort pair bundles cheapest-first so a small pair gets in
          // before an expensive one consumes the remaining budget.
          const sortedBundles = [...bundlesByKey.entries()].sort(
            (a, b) => a[1].bundleCost - b[1].bundleCost
          );
          for (const [, { siblings, bundleCost }] of sortedBundles) {
            if (bundleCost <= remaining) {
              siblings.forEach((sib) => {
                if (includedIds.has(sib.id)) return;
                included.push(sib);
                includedIds.add(sib.id);
              });
              remaining -= bundleCost;
            }
          }

          // --- Phase B: un-paired loose items ---
          const looseSorted = tier
            .filter(
              (i) => !i.pair_group_key && !includedIds.has(i.id)
            )
            .sort((a, b) => (a.price_inc_vat || 0) - (b.price_inc_vat || 0));
          for (const item of looseSorted) {
            const cost = item.price_inc_vat || 0;
            if (cost <= remaining) {
              included.push(item);
              includedIds.add(item.id);
              remaining -= cost;
            }
          }
          return;
        }

        // Fallback pass (allowPair=false): take whatever single side or
        // loose item still fits, cheapest-first.
        const sorted = [...tier].sort(
          (a, b) => (a.price_inc_vat || 0) - (b.price_inc_vat || 0)
        );
        for (const item of sorted) {
          if (includedIds.has(item.id)) continue;
          const cost = item.price_inc_vat || 0;
          if (cost <= remaining) {
            included.push(item);
            includedIds.add(item.id);
            remaining -= cost;
          }
        }
      };

      // Pass 1 (urgent) — strict pair bundling.
      takeFromTier(urgentItems, true);
      // Pass 2 (urgent) — fallback so a customer with a tight budget
      // still walks away with the most safety-critical single side
      // covered (better than skipping it entirely).
      takeFromTier(urgentItems, false);
      // Pass 3 (recommended) — strict pair bundling.
      takeFromTier(recommendedItems, true);
      // Pass 4 (recommended) — single-side fallback.
      takeFromTier(recommendedItems, false);

      return included;
    }
    
    // Manual selection
    return allItems.filter(item => approvedItems.has(item.id));
  };

  const selectedTotal = getSelectedTotal();

  // ============== HANDLERS ==============
  const handleSelectOption = (option) => {
    setSelectedOption(option);
    setShowBudgetInput(option === 'budget');
    setBudgetWarning(null);
    setBudgetProceedAnyway(false);
    
    if (option === 'all') {
      setApprovedItems(new Set(allItems.map(i => i.id)));
      // iter198aa — Celebrate bulk approval the first time per session.
      if (!bulkConfettiSeenRef.current && allItems.length > 0) {
        bulkConfettiSeenRef.current = true;
        bulkApprovalBurst();
        // Mark every item as already-celebrated so a subsequent
        // single-item approve doesn't double-pop.
        allItems.forEach((i) => confettiSeenRef.current.add(i.id));
      }
    } else if (option === 'essential') {
      setApprovedItems(new Set(essentialSafetyItems.map(i => i.id)));
      // iter198aa — Smaller bulk burst when essential-safety items
      // are present and we haven't yet celebrated this session.
      if (!bulkConfettiSeenRef.current && essentialSafetyItems.length > 0) {
        bulkConfettiSeenRef.current = true;
        bulkApprovalBurst();
        essentialSafetyItems.forEach((i) => confettiSeenRef.current.add(i.id));
      }
      // iter198p — Auto-decline tyre positions that are NOT essential
      // safety in any mixed-severity tyre group, so the customer's
      // "essential safety only" decision really means "MOT fail tyres
      // only", not "approve the whole group of 4 tyres".
      essentialSafetyItems.forEach((item) => {
        const declinePositions = _autoDeclinePositionsForEssential(item);
        if (declinePositions.length > 0) {
          setPerTyreDeclines((prev) => ({
            ...prev,
            [item.id]: declinePositions,
          }));
        }
      });
    } else if (option === 'budget') {
      setApprovedItems(new Set());
    }
  };
  
  // Toggle individual item expansion within summary boxes
  const toggleSummaryItemExpanded = (itemId) => {
    setExpandedSummaryItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  const handleApproveItem = (itemId, sourceEl = null) => {
    setApprovedItems(prev => new Set([...prev, itemId]));
    setDeclinedItems(prev => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    setDecideLaterItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
    // iter198aa — Tiny confetti the first time this item is approved
    // in the session. Doesn't replay on toggle-back-and-forth.
    if (!confettiSeenRef.current.has(itemId)) {
      confettiSeenRef.current.add(itemId);
      itemApprovalBurst(sourceEl);
    }
    // iter198k (Feb 2026) — Collapse the item after the customer
    // commits to a decision (approve / decline / decide later) so
    // the portal stays tidy as they work through the list. The
    // header still summarises the chosen state and they can tap
    // again to re-open if they want to change their mind.
    setExpandedSummaryItems(prev => ({ ...prev, [itemId]: false }));
    // Switch to manual mode if not already
    if (selectedOption && selectedOption !== 'budget') {
      setSelectedOption(null);
    }
  };
  
  const handleDecideLater = (itemId) => {
    setApprovedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
    setDecideLaterItems(prev => new Set([...prev, itemId]));
    // iter198k — Auto-collapse on decision (see handleApproveItem).
    setExpandedSummaryItems(prev => ({ ...prev, [itemId]: false }));
    // Switch to manual mode
    if (selectedOption) {
      setSelectedOption(null);
    }
  };
  
  // Decline reasons with customer-facing objection handling responses
  const DECLINE_REASONS = {
    cost: {
      label: 'Cost / Budget',
      response: "We understand budget is a real consideration. We can help you prioritise the most critical items, spread the work across visits, or discuss payment options. Your safety doesn't have to be all-or-nothing — let's find a way to keep you safe within your means."
    },
    credibility: {
      label: 'Not sure I need this',
      response: "That's a fair question. Everything we've flagged is documented with photos and technician notes so you can see exactly what we found. We're happy for you to get a second opinion — our findings are evidence-based and speak for themselves."
    },
    timing: {
      label: 'Not right now / Timing',
      response: "We can hold this quote for you and schedule the work when it suits. Just be aware that items flagged as urgent may worsen with continued driving — we'd rather a small job now than a bigger bill later."
    },
    third_party: {
      label: 'Want a second opinion',
      response: "Getting another view is completely reasonable. We can provide our full inspection report with photos for you to share with whoever you consult. Whatever you decide, the important thing is the work gets done safely."
    },
    usage: {
      label: "I don't use the car much",
      response: "Even on short or infrequent journeys, safety-critical components like brakes and steering affect every trip. We'd recommend at least addressing the urgent items — they don't know whether you're driving 5 miles or 50."
    },
    history: {
      label: 'This was done recently',
      response: "We understand it's frustrating when something comes up after recent work. What we're showing you is the current condition as found today — wear items can change between inspections, and our role is to report what we see right now."
    },
    warranty: {
      label: 'Should be under warranty',
      response: "It's definitely worth checking your warranty or breakdown cover. We can provide full documentation to support any claim. In the meantime, the safety-critical items still need a decision regardless of coverage."
    },
    emotional: {
      label: 'Feeling overwhelmed',
      response: "We completely understand — an unexpected repair bill is never welcome. There's no pressure at all. Take the time you need, and if you'd like to talk it through with us, we're just a phone call away. We're here to help, not to add stress."
    }
  };
  
  const handleDeclineItem = (itemId) => {
    // Remove from approved
    setApprovedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
    setDecideLaterItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
    // Show decline dropdown
    setShowDeclineDropdown(prev => ({ ...prev, [itemId]: true }));
    // iter199ak (Feb 2026) — Do NOT reset ``selectedOption`` here.
    // Previously declining a single amber item nuked the customer's
    // "Authorise All" / "Essential" / "Budget" selection because the
    // legacy logic equated any decline with a switch to "manual
    // mode". The Submit gate at the bottom of the page reads
    // ``approvedItems.size > 0 || selectedOption``, so wiping
    // ``selectedOption`` while ``approvedItems`` was already empty
    // (e.g. budget flow that hadn't yet materialised approvals)
    // hid the Confirm CTA entirely. The set of approved items
    // already updates above, so leaving ``selectedOption`` alone is
    // safe and preserves the customer's higher-level intent — they
    // chose "Authorise All except this one", not "I no longer want
    // the bulk option at all".
  };
  
  const handleSelectDeclineReason = (itemId, reason) => {
    setDeclinedItems(prev => ({ ...prev, [itemId]: reason }));
    setShowDeclineDropdown(prev => ({ ...prev, [itemId]: false }));
    // iter198vv (Feb 2026) — Do NOT auto-collapse on decline.
    // Previously this fired `setExpandedSummaryItems(false)` which
    // collapsed the card — and the objection-handling response
    // panel lives INSIDE the expanded body. Result: customers
    // picked a reason and the card vanished without showing them
    // the response we'd prepared. Keep the card expanded so the
    // response is the next thing they see; the per-item "Change
    // mind" button still lets them undo.
    setExpandedSummaryItems(prev => ({ ...prev, [itemId]: true }));
  };
  
  const handleUndoDecline = (itemId) => {
    setDeclinedItems(prev => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
    setShowDeclineDropdown(prev => ({ ...prev, [itemId]: false }));
  };
  
  const handleBudgetChange = (value) => {
    setBudgetAmount(value);
    const budget = parseFloat(value) || 0;
    
    // iter198p — Compare budget against ESSENTIAL SAFETY total (MOT
    // fails only), not the broader urgent total. Advisory tyres and
    // engine-recommended items are not legally required.
    if (budget < essentialSafetyTotal && budget > 0) {
      setBudgetWarning({
        message: 'Your selected budget does not cover all essential safety items (MOT failures). These items must be addressed before the vehicle can be driven legally.',
        recommended: essentialSafetyTotal,
        selected: budget
      });
    } else {
      setBudgetWarning(null);
    }
    
    // Update approved items based on budget
    const included = getIncludedItems();
    setApprovedItems(new Set(included.map(i => i.id)));
  };
  
  const handleBudgetProceed = (increase) => {
    if (increase) {
      setBudgetAmount(urgentTotal.toFixed(2));
      setBudgetWarning(null);
    } else {
      setBudgetProceedAnyway(true);
    }
  };
  
  const toggleExplanation = (itemId) => {
    setExpandedExplanations(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  const handleSubmit = async () => {
    if (!customerName.trim()) {
      alert('Please enter your name');
      return;
    }
    if (!typedSignature.trim()) {
      alert('Please sign to confirm');
      return;
    }
    
    setSubmitting(true);
    try {
      const includedItems = getIncludedItems();
      const excludedItems = allItems.filter(item => !includedItems.some(i => i.id === item.id));
      
      // Determine decision type based on selection
      let decisionType = 'decline';
      if (includedItems.length === allItems.length) {
        decisionType = 'approve_all';
      } else if (includedItems.length > 0) {
        if (selectedOption === 'essential') {
          decisionType = 'approve_safety_only';
        } else if (selectedOption === 'budget') {
          decisionType = 'prioritise_within_budget';
        } else {
          // Manual selection - if only essential safety items selected, treat as safety only
          const onlyEssentialSelected = includedItems.every(item => essentialSafetyItems.some(u => u.id === item.id));
          decisionType = onlyEssentialSelected ? 'approve_safety_only' : 'prioritise_within_budget';
        }
      }
      
      // Build API request matching CustomerApprovalInput
      const requestData = {
        decision_type: decisionType,
        customer_name: customerName,
        signature_type: 'typed',
        signature_data: typedSignature,
        item_approvals: includedItems.map(item => ({
          item_id: item.id,
          approved: true,
          item_label: item.label,
          price_inc_vat: item.price_inc_vat,
          // Iter189 — per-child positions the customer declined inside
          // a grouped tyre repair. Empty array = whole group approved.
          declined_child_positions: perTyreDeclines[item.id] || [],
          // iter204d — Positions the customer chose the £25 puncture
          // repair for instead of a full tyre replacement. FOH uses
          // this to swap the workshop scope when the job is created.
          repair_child_positions: perTyreRepairs[item.id] || [],
        }))
      };
      
      // Add budget prioritisation if using budget flow
      if (selectedOption === 'budget' || decisionType === 'prioritise_within_budget') {
        // iter211v — payload must match backend ``BudgetPrioritisation``
        // Pydantic model exactly, otherwise the whole submit 422s and
        // the customer sees "Failed to submit". The model requires:
        //   included_items / excluded_items  → List[{id,label,price,category}]
        //   red_items_count / amber_included_count / amber_excluded_count → ints
        // Previously we sent bare string arrays + omitted the counts, so
        // any subset-approval submit was silently rejected server-side.
        const _catFor = (it) => (
          (urgentItems || []).some((u) => u.id === it.id) ? "red" : "amber"
        );
        const _toBpItem = (it) => ({
          id:       it.id,
          label:    it.label || "",
          price:    Number(it.price_inc_vat || 0),
          category: _catFor(it),
        });
        const _incFull = includedItems.map(_toBpItem);
        const _excFull = excludedItems.map(_toBpItem);
        requestData.budget_prioritisation = {
          budget_entered:        parseFloat(budgetAmount) || selectedTotal,
          total_authorised:      selectedTotal,
          included_items:        _incFull,
          excluded_items:        _excFull,
          red_items_count:       _incFull.filter((i) => i.category === "red").length
                               + _excFull.filter((i) => i.category === "red").length,
          amber_included_count:  _incFull.filter((i) => i.category === "amber").length,
          amber_excluded_count:  _excFull.filter((i) => i.category === "amber").length,
        };
      }
      
      // Add declined items with reasons
      if (Object.keys(declinedItems).length > 0) {
        requestData.declined_items = Object.entries(declinedItems).map(([itemId, reason]) => {
          const item = allItems.find(i => i.id === itemId);
          return {
            item_id: itemId,
            item_label: item?.label || '',
            decline_reason: reason,
            price_inc_vat: item?.price_inc_vat || 0
          };
        });
      }

      // iter199ak (Feb 2026) — Backend validates that
      // ``decision_type === 'decline'`` carries a top-level
      // ``rejection_reason`` (server.py:29015). When the customer
      // declined every item via the per-item decline dropdowns we
      // already have a structured ``declined_items`` array but the
      // legacy payload didn't surface a rolled-up ``rejection_reason``
      // — so the submit failed with a 400 and the customer saw a
      // generic error toast. Roll up the per-item reasons into a
      // single readable summary so the request succeeds and the FOH
      // gets a clean audit trail.
      if (decisionType === "decline" && !requestData.rejection_reason) {
        const reasonsRollup = (requestData.declined_items || [])
          .map((d) => d.decline_reason)
          .filter(Boolean);
        requestData.rejection_reason = reasonsRollup.length
          ? `Per-item declines: ${[...new Set(reasonsRollup)].join("; ")}`
          : "Customer declined all recommended work";
      }
      
      await axios.post(`${API}/customer/approval/${token}/submit`, requestData);
      // iter198cc (Feb 2026) — Snapshot the authorised amount/items so
      // the "Thank You" confirmation screen can echo what the customer
      // just committed to. Closes the conversion loop with a concrete
      // figure ("£429.19 of work — your slot is secured") rather than a
      // vague "we've received your response".
      setAuthorisedSnapshot({
        total:        selectedTotal,
        additional:   Math.max(0, selectedTotal - alreadyAuthorisedValue - (chargesTotalIncVat || 0)),
        item_count:   includedItems.length,
        decision:     decisionType,
      });
      // iter198cc — Final celebration burst when the customer actually
      // signs off. Skip when the customer declined everything (no work
      // to celebrate) or in reduced-motion mode (handled by the helper).
      if (includedItems.length > 0) {
        bulkApprovalBurst();
      }
      setSubmitted(true);
    } catch (err) {
      // iter198dd (Feb 2026) — Surface the backend's actual reason
      // instead of a generic "try again". The previous catch hid
      // useful messages like "already responded" and "DRE blocked".
      console.error('Submit error:', err);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 400 && typeof detail === 'string') {
        msg = detail;
      } else if (status === 404) {
        msg = 'This authorisation link is no longer valid. Please contact the garage for a fresh link.';
      } else if (status >= 500) {
        msg = `Server error (${status}). Please try again in a moment.`;
      } else if (err?.message === 'Network Error' || !err?.response) {
        msg = 'Connection issue. Please check your internet and try again — your selections are still here.';
      } else {
        msg = typeof detail === 'string' ? detail : 'Failed to submit. Please try again or contact the garage.';
      }
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ============== RENDER ==============
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-slate-300 border-t-slate-600 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-slate-600">Loading your inspection results...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 max-w-sm text-center shadow-sm" data-testid="portal-error-screen">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Link Error</h2>
          <p className="text-slate-600 text-sm mb-4">{error}</p>
          {/* iter198bb — Recover from transient network errors without
              forcing the customer to contact the garage. */}
          <button
            type="button"
            onClick={() => { setError(null); setLoading(true); fetchData(); }}
            className="w-full py-2.5 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
            data-testid="portal-error-retry-btn"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  
  if (submitted) {
    // iter198cc — Concrete confirmation: show the authorised total,
    // item count and a "slot is secured" reassurance. Falls back to
    // the simple Thank You message if the snapshot is missing
    // (decline-all path, or refresh after submit).
    const snap = authorisedSnapshot || {};
    const showFinancials = (snap.item_count || 0) > 0 && (snap.additional || 0) > 0;
    const customerFirstName = (customerName || '').trim().split(' ')[0];
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl p-7 max-w-md text-center shadow-sm border border-emerald-100"
          data-testid="portal-submitted-screen"
        >
          <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 ring-4 ring-emerald-50">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" strokeWidth={2.5} />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-1">
            {customerFirstName ? `Thank you, ${customerFirstName}` : 'Thank you'}
          </h2>
          {showFinancials ? (
            <>
              <p
                className="text-slate-700 text-sm mb-4"
                data-testid="portal-submitted-summary"
              >
                You've authorised{' '}
                <span className="font-bold text-emerald-700" data-testid="portal-submitted-additional">
                  £{(snap.additional || 0).toFixed(2)}
                </span>{' '}
                of additional work — your slot is secured and we'll get straight on with it.
              </p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 text-left">
                <div className="flex items-center justify-between text-xs text-emerald-900 mb-1.5">
                  <span>Items authorised</span>
                  <span className="font-semibold" data-testid="portal-submitted-item-count">
                    {snap.item_count}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-emerald-900 mb-1.5">
                  <span>Additional work</span>
                  <span className="font-semibold">£{(snap.additional || 0).toFixed(2)}</span>
                </div>
                <div className="border-t border-emerald-200 my-2"></div>
                <div className="flex items-center justify-between text-sm text-slate-900">
                  <span className="font-semibold">Total invoice</span>
                  <span className="font-bold" data-testid="portal-submitted-total">
                    £{(snap.total || 0).toFixed(2)}
                  </span>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                A confirmation will be sent to you. We'll be in touch shortly with collection details.
              </p>
            </>
          ) : (
            <p className="text-slate-600 text-sm">
              Your response has been received. We'll be in touch shortly to confirm the next steps.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-72">
      {/* iter211g — Bumped outer padding from pb-24 (96px) to pb-72
          (288px). The fixed sticky bottom bar now carries a 3-column
          totals row + a decision summary strip + the sticky
          "Continue to Confirm" CTA — total height easily 200-220px.
          The old 96px reserve meant the Authorise Work button (and
          often the signature input) rendered BEHIND the sticky bar
          after "Continue to Confirm" scrolled the section into view,
          which is what customers were reporting as "button doesn't
          work". */}
      {/* ============== TOP SECTION ============== */}
      <div className="bg-white border-b border-slate-200">
        {/* Garage Branding — iter197kk: bumped logo size from h-8 to
            h-16 so the garage brand carries appropriate weight at the
            top of the customer portal. Container padding also grown so
            the logo isn't cramped against the divider. Multi-tenant
            ready: the logo URL comes from /public/garage/logo which
            returns the active tenant's logo. */}
        {(garageLogo || garageName) && (
          <div className="px-4 py-5 border-b border-slate-100 relative flex items-center justify-center">
            {garageLogo && (
              <img
                src={garageLogo}
                alt={garageName || 'Garage'}
                className="h-16 sm:h-20 max-w-[260px] object-contain"
                data-testid="portal-garage-logo"
              />
            )}
            {!garageLogo && garageName && (
              <span
                className="text-xl font-bold text-slate-800 tracking-tight"
                data-testid="portal-garage-name"
              >
                {garageName}
              </span>
            )}
            {/* iter197mm — One-tap share for the customer-portal quote
                URL. Sits absolutely positioned in the top-right of the
                branding bar so it doesn't disturb the logo's centred
                alignment. Native share sheet on mobile, dropdown
                fallback (WhatsApp / Email / Copy link) on desktop. */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <ShareQuoteButton
                garageName={garageName}
                vehicleReg={data?.vehicle?.reg || data?.vehicle_reg}
              />
            </div>
          </div>
        )}
        {/* iter197mm — Fallback share row when the garage hasn't set a
            logo or name yet (e.g. fresh tenant). Keeps the share
            feature universally available. */}
        {!garageLogo && !garageName && (
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-end">
            <ShareQuoteButton
              garageName={garageName}
              vehicleReg={data?.vehicle?.reg || data?.vehicle_reg}
            />
          </div>
        )}
        
        {/* Vehicle Details — iter197ll: vehicle reg now rendered as a
            UK-number-plate visual (yellow background, black mono
            text) so the portal reads like a personalised branded
            receipt rather than a web form. Make/model and mileage
            sit alongside in a balanced 2-column layout. */}
        <div className="px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="flex-shrink-0 flex items-center justify-center px-3 py-1.5 rounded-md bg-yellow-300 border border-yellow-500 shadow-inner ring-1 ring-black/10"
              data-testid="portal-vehicle-reg-plate"
            >
              <span
                className="font-bold text-slate-900 tracking-widest text-lg sm:text-xl uppercase"
                style={{ fontFamily: '"Courier New", Menlo, Consolas, monospace' }}
              >
                {data?.vehicle?.reg || data?.vehicle_reg || '— — — —'}
              </span>
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 text-base sm:text-lg leading-tight truncate">
                {formatVehicleName(
                  data?.vehicle?.make || data?.vehicle_make,
                  data?.vehicle?.model || data?.vehicle_model,
                ) || 'Your Vehicle'}
              </p>
              {data?.mileage && (
                <p className="text-xs sm:text-sm text-slate-500">
                  {data.mileage.toLocaleString()} miles
                </p>
              )}
              {data?.customer_name && (
                <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
                  Prepared for <span className="font-medium text-slate-800">{data.customer_name}</span>
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-testid="portal-visit-complete-badge">
            <CheckCircle2 className="w-4 h-4" />
            <span>{data?.visit_type || 'Safety & Reliability Inspection'} Complete</span>
          </div>
        </div>
        
        {/* Introduction Text — iter197nn / iter197oo trust-first redesign.
            • Max 3 sentences, max 50 words, <10s read.
            • No legal / penalty / compliance language at this layer.
            • Visit-type aware (MOT / Service / Diagnostic / combos)
              using the backend-resolved `data.visit_type` field.
            • Trust statement appended subtly below the greeting.
            iter197oo — Always rendered. The legacy intent-mode intro
            (intentConfig.intro_text) carried legal-compliance language
            ("minimum legal standard for road use", "MOT compliance")
            that the new trust-first spec explicitly bans. The intent
            mode itself still drives downstream presentation; only the
            intro text is now standardised across every journey. */}
        <div className="px-4 pb-4">
          <div data-testid="portal-trust-intro">
            <p className="text-sm text-slate-700 leading-relaxed">
              Thank you for choosing <span className="font-semibold text-slate-900">{garageName || 'us'}</span>.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed mt-1.5">
              We've now completed the <span className="font-medium text-slate-900">{data?.visit_type || 'Safety & Reliability Inspection'}</span> on your vehicle.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed mt-1.5">
              The results below provide a clear summary of today's findings together with any recommendations.
            </p>
          </div>
          {/* Persistent trust statement — single sentence, subtle
              visual weight, present on every portal. */}
          <p
            className="mt-3 text-[12.5px] italic text-slate-500 leading-snug"
            data-testid="portal-trust-statement"
          >
            All recommendations are supported by inspection findings, measurements, test results or manufacturer service requirements where applicable.
          </p>
        </div>
        
        {/* ============== PRICING CLARITY HEADER ============== */}
        {/* Shows: Work already agreed | Additional work selected | Total if approved
            iter197c — Workshop charges & disposal are NOT surfaced in
            the clarity header at the top of the portal. They appear
            ONLY in the dedicated charges-summary block further down
            (and not at all when FOH has deselected them). "Additional
            work selected" reflects the customer's actual selections —
            charges are baked into the Total via chargesTotalIncVat. */}
        <div className="px-4 pb-4">
          <div className="bg-slate-50 rounded-xl p-4 space-y-2 border border-slate-200" data-testid="portal-pricing-clarity-header">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                {data?.intent_config?.already_authorised_label
                  ? `${data.intent_config.already_authorised_label} (already booked)`
                  : "Work already agreed"}
              </span>
              <span className="font-semibold text-slate-800" data-testid="portal-work-already-agreed-value">£{alreadyAuthorisedValue.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Additional work selected</span>
              <span className="font-semibold text-blue-700" data-testid="portal-additional-work-selected">
                £{Math.max(0, selectedTotal - alreadyAuthorisedValue - (chargesTotalIncVat || 0)).toFixed(2)}
              </span>
            </div>
            <div className="h-px bg-slate-200" />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Total if approved</span>
              <span className="font-bold text-lg text-slate-900" data-testid="portal-total-if-approved">£{selectedTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* The "Included in your quote" (consumables / environmental
            disposal) box was previously rendered HERE in the pricing
            clarity header. It has been moved further down so it sits
            UNDER the pricing options — see the
            `portal-charges-summary` block right before the bottom
            sticky totals bar. */}
      </div>

      {/* ============== IMMEDIATE SUMMARY - EXPANDABLE BOXES ============== */}
      <div className="px-4 py-5 bg-white border-b border-slate-200 mt-2">
        {/* iter197n — Battery gateway customer advisory. Surfaced ONLY when
            the diagnostic technician recorded a MARGINAL or FAIL battery
            test. Backend only sets `battery_advisory` when interpretation
            blocks reliable findings — so just rendering it inline is safe. */}
        {data?.battery_advisory?.advisory_text && (
          <div
            className="mb-3 rounded-xl border-2 border-amber-400 bg-amber-50 p-4"
            data-testid="portal-battery-advisory"
          >
            <div className="flex items-start gap-3">
              <Battery className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-amber-900">
                    Battery test result: {data.battery_advisory.interpretation_label?.toUpperCase()}
                  </h3>
                  {data.battery_advisory.reading && (
                    <span className="text-xs bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full font-mono" data-testid="portal-battery-reading">
                      {data.battery_advisory.reading}
                    </span>
                  )}
                </div>
                <p
                  className="text-sm text-amber-900 mt-2 leading-relaxed"
                  data-testid="portal-battery-advisory-text"
                >
                  {data.battery_advisory.advisory_text}
                </p>
                <p className="text-xs text-amber-800 mt-2 italic">
                  Pre-requisite to further meaningful diagnostic work — please discuss with your service advisor before authorising additional investigation.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* iter197aa — Repair Integrity Engine, charging-system banner.
            Surfaces ONLY when the yestimate carries an alternator or
            starter motor JG. Calm, friendly tone — the goal is to help
            the customer understand WHY both items are presented
            together, not to alarm them. */}
        {data.repair_integrity && data.repair_integrity.status !== "complete" && (
          <div
            className={`mb-3 rounded-xl border-2 p-4 ${
              data.repair_integrity.status === "warranty_risk"
                ? "border-red-300 bg-red-50"
                : "border-amber-300 bg-amber-50"
            }`}
            data-testid="portal-repair-integrity"
            data-status={data.repair_integrity.status}
          >
            <div className="flex items-start gap-3">
              <ShieldAlert
                className={`w-6 h-6 shrink-0 mt-0.5 ${
                  data.repair_integrity.status === "warranty_risk"
                    ? "text-red-700"
                    : "text-amber-700"
                }`}
              />
              <div className="flex-1 min-w-0">
                <h3
                  className={`font-bold ${
                    data.repair_integrity.status === "warranty_risk"
                      ? "text-red-900"
                      : "text-amber-900"
                  }`}
                  data-testid="portal-rie-headline"
                >
                  Charging System Repair Package
                </h3>
                <p
                  className={`text-sm mt-2 leading-relaxed ${
                    data.repair_integrity.status === "warranty_risk"
                      ? "text-red-900"
                      : "text-amber-900"
                  }`}
                  data-testid="portal-rie-body"
                >
                  {data.repair_integrity.headline}
                </p>
                {data.repair_integrity.ack_required?.kind === "typed_name" && (
                  <p className="text-xs text-red-800 mt-2 italic font-medium">
                    If you wish to proceed without the battery replacement,
                    you will be asked to type your name to confirm — this
                    helps us protect your warranty on the new component.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Top-of-list dangerous banner — only when ≥1 dangerous defect
            exists. Visible BEFORE the urgent box, so the customer sees
            the safety risk on glance even before tapping anything. */}
        {urgentItems.some((it) => it.status === 'dangerous' || it.is_dangerous === true) && (
          <DangerousAcknowledgeBanner
            tokenId={data?.token || token}
          />
        )}
        {/* iter197nn — Customer Control Statement. Single, calm,
            no-pressure reminder placed once, immediately before the
            first recommendation block. Reinforces that approving work
            is the customer's choice and that questions are welcomed. */}
        {(urgentItems.length > 0 || recommendedItems.length > 0) && (
          <div
            className="mx-1 mb-3 px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200"
            data-testid="portal-customer-control-statement"
          >
            <p className="text-[12.5px] leading-snug text-slate-700">
              You remain in full control of which work is authorised. If you have any questions about any recommendation, our team will be happy to explain it further.
            </p>
          </div>
        )}
        <p className="text-xs text-slate-500 text-center mb-3 italic">
          Tap each item to expand for details — tap again to collapse
        </p>
        <div className="space-y-3">
          {/* iter211g — Diagnostic → Rectification grouped cards. When
              a diag finding has been paired with a rectification (from
              the tech-side picker or the FOH prompt), render them as
              ONE grouped invoice-style card so the customer reads
              them as an inseparable unit ("investigation charge +
              fix"). Rendered above the standard urgent / recommended
              lists to make them the first thing the customer sees. */}
          {rectificationGroups && rectificationGroups.length > 0 && (
            <div className="space-y-2" data-testid="portal-rectification-groups">
              {rectificationGroups.map((g) => (
                <RectificationGroupCard
                  key={g.rect.id}
                  token={token}
                  diagJG={g.diag}
                  rectJG={g.rect}
                  approvedIds={approvedItems}
                  onApprove={(id) => handleApproveItem(id, null)}
                  onSkip={(id) => handleDeclineItem(id, null)}
                  onVariantChanged={(updatedRectJG) => {
                    // Merge the swapped variant back into local state.
                    setRectificationGroups((prev) => prev.map((row) =>
                      row.rect.id === updatedRectJG.id
                        ? { ...row, rect: {
                            ...row.rect,
                            ...updatedRectJG,
                            variants: row.rect.variants, // preserve picker options
                          } }
                        : row
                    ));
                    // Refetch main data so totals re-roll everywhere.
                    fetchData();
                  }}
                />
              ))}
            </div>
          )}
          {/* Urgent Items - Expandable Box */}
          {urgentItems.length > 0 && (
            <div className="rounded-xl border-2 border-red-300 bg-red-50 overflow-hidden">
              {/* Clickable Header — iter197nn trust-first wording.
                  Two-line label: "Urgent Attention Required" headline
                  + small count subline. No alarmist phrasing. */}
              <button
                onClick={() => setUrgentSummaryExpanded(!urgentSummaryExpanded)}
                className="w-full flex items-center justify-between p-3 hover:bg-red-100 transition-colors"
                data-testid="urgent-summary-toggle"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <div className="text-left">
                    <span className="block text-red-900 font-semibold leading-tight">
                      Urgent Attention Required
                    </span>
                    <span className="block text-[11.5px] text-red-700 leading-tight mt-0.5">
                      {urgentItems.length} {urgentItems.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-900" data-testid="urgent-total-amount">
                    £{urgentTotal.toFixed(2)}
                    {urgentHasPending && (
                      <span className="ml-1 text-[10.5px] font-black uppercase tracking-wider bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded align-middle">
                        + Price TBC
                      </span>
                    )}
                  </span>
                  {urgentSummaryExpanded ? (
                    <ChevronDown className="w-5 h-5 text-red-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-red-600" />
                  )}
                </div>
              </button>
              
              {/* Expanded Content - Item Details (Level 2 Accordion) */}
              {urgentSummaryExpanded && (
                <div className="border-t border-red-200 bg-red-50/50 p-3 space-y-2">
                  {_visibleUrgentItems.map(item => {
                    const isDangerous = item.status === 'dangerous' || item.is_dangerous === true;
                    const isExpanded = expandedSummaryItems[item.id];
                    const isApproved = approvedItems.has(item.id);
                    // iter198z (Feb 2026) — Visual feedback on approval state.
                    // Approved items render in green, declined items in red so
                    // the customer can scan the list and see at a glance what
                    // they've authorised.
                    const isDeclined = !isApproved && !!declinedItems?.[item.id];
                    const aiExplanation = aiExplanations[item.id];

                    // Iter187 — structured tyre group card. Replaces
                    // the flat per-tyre invoice line with the calm,
                    // severity-layered "Tyre Safety & Wear Issues
                    // Identified" card.
                    if (item.tyre_group_subtype === 'tyre_replacement_group') {
                      const _touchAll = (
                        selectedOption === 'all'
                        || (selectedOption === 'essential'
                            && (item.is_essential_safety || perTyreDeclines[item.id]?.length))
                      );
                      // iter198u (Feb 2026) — The React key only
                      // changes when a TOP-LEVEL option flips. Per-
                      // tyre clicks update perTyreApproved /
                      // perTyreDeclines in the parent but do NOT
                      // remount the child — that previously wiped
                      // out previously-approved positions whenever
                      // the customer toggled a different tyre's
                      // Decline button. The child preserves its
                      // touched/declined sets across customer clicks;
                      // remount only happens for the rare event of
                      // switching between "Complete all", "Essential
                      // safety", "Budget", or manual modes.
                      const _optionKey = selectedOption || 'manual';
                      return (
                        <TyrePortalCard
                          key={`${item.id}::opt=${_optionKey}::touchAll=${_touchAll ? 'T' : 'F'}`}
                          item={item}
                          isApproved={isApproved}
                          isDangerous={isDangerous}
                          onPerTyreDecisionChange={handlePerTyreDecisionChange}
                          forceDeclinedPositions={perTyreDeclines[item.id]}
                          forceApprovedPositions={perTyreApproved[item.id]}
                          forceTouchAll={_touchAll}
                        />
                      );
                    }
                    
                    return (
                      <div 
                        key={item.id}
                        className={`rounded-xl overflow-hidden transition-all ring-1 ring-transparent ${
                          isDeclined
                            ? 'bg-rose-50 border border-rose-300 ring-rose-200'
                            : isApproved
                            ? 'bg-emerald-50 border border-emerald-300 ring-emerald-200'
                            : isDangerous 
                            ? 'bg-slate-900 border border-slate-700' 
                            : 'bg-white border border-red-200'
                        }`}
                        data-testid={`portal-item-card-${item.id}`}
                        data-approval-state={isApproved ? 'approved' : isDeclined ? 'declined' : 'pending'}
                      >
                        {/* Clickable Header Row */}
                        <button
                          onClick={() => toggleSummaryItemExpanded(item.id)}
                          className={`w-full p-3 flex items-center justify-between hover:opacity-90 transition-opacity ${
                            isDangerous ? 'hover:bg-slate-800' : 'hover:bg-red-50'
                          }`}
                          data-testid={`summary-item-toggle-${item.id}`}
                        >
                          <div className="flex-1 text-left min-w-0">
                            {/* Dangerous defects get a prominent animated
                                warning chip ABOVE the title so the customer
                                cannot miss it on glance, even when the card
                                is collapsed inside the urgent list. */}
                            {isDangerous && (
                              <div
                                className="inline-flex items-center gap-1.5 bg-yellow-400 text-red-800 px-2 py-0.5 rounded mb-1.5 ring-2 ring-yellow-200 animate-pulse"
                                data-testid={`dangerous-chip-${item.id}`}
                              >
                                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.5} />
                                <span className="text-[10px] font-black uppercase tracking-wider">
                                  Dangerous · Do Not Drive
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <span className={`font-medium text-sm ${isDangerous && !isApproved && !isDeclined ? 'text-white' : 'text-slate-900'}`}>
                                {item.label}
                              </span>
                              {isApproved && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wide"
                                  data-testid={`portal-item-approved-badge-${item.id}`}
                                >
                                  <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                                  Approved
                                </span>
                              )}
                              {isDeclined && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-wide"
                                  data-testid={`portal-item-declined-badge-${item.id}`}
                                >
                                  <XCircle className="w-3 h-3" strokeWidth={2.5} />
                                  Skipped
                                </span>
                              )}
                            </div>
                            {!isExpanded && (
                              <p className={`text-xs mt-1 ${isDangerous ? 'text-slate-400' : 'text-slate-500'}`}>
                                Tap for details
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`font-bold text-sm ${
                              isApproved ? 'text-emerald-700'
                              : isDeclined ? 'text-slate-400 line-through'
                              : isDangerous ? 'text-white'
                              : 'text-red-700'
                            }`}>
                              £{(item.price_inc_vat || 0).toFixed(2)}
                            </span>
                            {isExpanded ? (
                              <ChevronDown className={`w-4 h-4 ${isDangerous ? 'text-slate-400' : 'text-red-500'}`} />
                            ) : (
                              <ChevronRight className={`w-4 h-4 ${isDangerous ? 'text-slate-400' : 'text-red-500'}`} />
                            )}
                          </div>
                        </button>
                        
                        {/* Expanded Content - Full Card Details */}
                        {isExpanded && (
                          <div className={`px-3 pb-3 pt-2 border-t ${
                            isDangerous ? 'border-slate-700 bg-slate-800' : 'border-red-100 bg-red-50/30'
                          }`}>
                            {/* iter197p — Primary customer narrative
                                from the Customer Advice Engine (CAE).
                                When present, REPLACES the 4-section
                                structured explanation as the primary
                                paragraph. Legacy 4-section block stays
                                as fallback for items without CAE. */}
                            {/* iter198w (Feb 2026) — Customer advice
                                paragraph (CAE-generated). Previously
                                this was rendered EITHER the CAE
                                paragraph OR the structured 4-stage
                                explanation, never both. Per product:
                                show the structured explanation FIRST
                                (evidence-led trust), then surface the
                                AI customer advice paragraph below as a
                                "More from your service advisor"
                                callout — applies to every item that
                                has a generated customer_advice.summary
                                (shocks, alignment, brakes, etc.). */}
                            {item.explanation?.has_structured_explanation ? (
                              item.customer_advice?.summary ? (
                                /* iter211d — CAE summary already covers
                                   what the 4-section block says; hide
                                   behind a "Technical detail" toggle so
                                   the CAE paragraph is the customer's
                                   primary read. */
                                <details
                                  className="mb-3"
                                  data-testid={`structured-explanation-toggle-${item.id}`}
                                >
                                  <summary className={`cursor-pointer text-xs font-medium select-none py-1 ${isDangerous ? 'text-slate-300 hover:text-slate-100' : 'text-slate-500 hover:text-slate-700'}`}>
                                    Technical detail
                                  </summary>
                                  <div className="space-y-3 mt-2" data-testid={`structured-explanation-${item.id}`}>
                                    <div>
                                      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDangerous ? 'text-slate-400' : 'text-slate-500'}`}>What we found</p>
                                      <p className={`text-sm leading-relaxed ${isDangerous ? 'text-slate-200' : 'text-slate-700'}`}>{item.explanation.what_found || item.notes}</p>
                                    </div>
                                    <div>
                                      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDangerous ? 'text-amber-400' : 'text-red-600'}`}>What this means</p>
                                      <p className={`text-sm leading-relaxed ${isDangerous ? 'text-slate-200' : 'text-slate-700'}`}>{item.explanation.what_means}</p>
                                    </div>
                                    <div className={`p-2.5 rounded-lg ${isDangerous ? 'bg-slate-700' : 'bg-red-100/60'}`}>
                                      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDangerous ? 'text-white' : 'text-red-800'}`}>What's required</p>
                                      <p className={`text-sm leading-relaxed font-medium ${isDangerous ? 'text-slate-100' : 'text-slate-800'}`}>{item.explanation.whats_required || item.explanation.what_required}</p>
                                    </div>
                                    <div className={`p-2.5 rounded-lg border ${isDangerous ? 'bg-yellow-900/20 border-yellow-700/30' : 'bg-amber-50 border-amber-200'}`}>
                                      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDangerous ? 'text-yellow-400' : 'text-amber-700'}`}>What happens if not addressed</p>
                                      <p className={`text-sm leading-relaxed ${isDangerous ? 'text-yellow-100' : 'text-amber-800'}`}>{item.explanation.consequence || item.explanation.consequences}</p>
                                    </div>
                                  </div>
                                </details>
                              ) : (
                              <div className="space-y-3 mb-3" data-testid={`structured-explanation-${item.id}`}>
                                {/* WHAT WE FOUND */}
                                <div>
                                  <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDangerous ? 'text-slate-400' : 'text-slate-500'}`}>
                                    What we found
                                  </p>
                                  <p className={`text-sm leading-relaxed ${isDangerous ? 'text-slate-200' : 'text-slate-700'}`}>
                                    {item.explanation.what_found || item.notes}
                                  </p>
                                </div>
                                
                                {/* WHAT THIS MEANS */}
                                <div>
                                  <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDangerous ? 'text-amber-400' : 'text-red-600'}`}>
                                    What this means
                                  </p>
                                  <p className={`text-sm leading-relaxed ${isDangerous ? 'text-slate-200' : 'text-slate-700'}`}>
                                    {item.explanation.what_means}
                                  </p>
                                </div>
                                
                                {/* WHAT'S REQUIRED */}
                                <div className={`p-2.5 rounded-lg ${isDangerous ? 'bg-slate-700' : 'bg-red-100/60'}`}>
                                  <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDangerous ? 'text-white' : 'text-red-800'}`}>
                                    What's required
                                  </p>
                                  <p className={`text-sm leading-relaxed font-medium ${isDangerous ? 'text-slate-100' : 'text-slate-800'}`}>
                                    {item.explanation.whats_required || item.explanation.what_required}
                                  </p>
                                </div>
                                
                                {/* WHAT HAPPENS IF NOT ADDRESSED */}
                                <div className={`p-2.5 rounded-lg border ${
                                  isDangerous 
                                    ? 'bg-yellow-900/20 border-yellow-700/30' 
                                    : 'bg-amber-50 border-amber-200'
                                }`}>
                                  <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDangerous ? 'text-yellow-400' : 'text-amber-700'}`}>
                                    What happens if not addressed
                                  </p>
                                  <p className={`text-sm leading-relaxed ${isDangerous ? 'text-yellow-100' : 'text-amber-800'}`}>
                                    {item.explanation.consequence || item.explanation.consequences}
                                  </p>
                                </div>
                              </div>
                              )
                            ) : (
                              /* Fallback for items without structured explanation */
                              <div className="space-y-2 mb-3">
                                <p className={`text-sm ${isDangerous ? 'text-slate-300' : 'text-red-700'}`}>
                                  {item.notes || 'This item requires attention.'}
                                </p>
                              </div>
                            )}

                            {/* iter198w (Feb 2026) — Customer advice
                                paragraph callout. Sits below the
                                structured 4-stage explanation so the
                                customer first sees the evidence-led
                                summary, then a personalised "more from
                                your service advisor" note. Applies to
                                every item that carries a generated
                                customer_advice.summary (shocks,
                                alignment, brakes, etc.). */}
                            {item.customer_advice?.summary && (
                              <div
                                className={`space-y-2 mb-3 p-3 rounded-lg border-l-4 ${
                                  isDangerous
                                    ? 'bg-slate-700/60 border-amber-300'
                                    : 'bg-indigo-50/70 border-indigo-400'
                                }`}
                                data-testid={`cae-portal-${item.id}`}
                              >
                                <p className={`text-[11px] font-bold uppercase tracking-wider ${isDangerous ? 'text-amber-300' : 'text-indigo-700'}`}>
                                  More from your service advisor
                                </p>
                                <p
                                  className={`text-sm leading-relaxed ${isDangerous ? 'text-slate-100' : 'text-slate-800'}`}
                                  data-testid={`cae-portal-summary-${item.id}`}
                                >
                                  {item.customer_advice.summary}
                                </p>
                                {item.customer_advice.expanded && (
                                  <details
                                    className={`text-xs ${isDangerous ? 'text-slate-300' : 'text-slate-600'}`}
                                    data-testid={`cae-portal-expanded-toggle-${item.id}`}
                                  >
                                    <summary className={`cursor-pointer font-medium ${isDangerous ? 'text-amber-300 hover:text-amber-200' : 'text-indigo-700 hover:text-indigo-900'}`}>
                                      Read more
                                    </summary>
                                    <p className="mt-2 leading-relaxed" data-testid={`cae-portal-expanded-${item.id}`}>
                                      {item.customer_advice.expanded}
                                    </p>
                                  </details>
                                )}
                              </div>
                            )}

                            {/* iter197ff — Standard advisories (auto-derived
                                rule-engine notes + aide-mémoir templates).
                                Previously only rendered in the dead
                                `expandedExplanation` branch, so the customer
                                never saw the "If the discs measure below
                                spec…" / "Caliper may be seized…" paragraphs
                                even though FOH had ticked them. Surface
                                them here, immediately after the structured
                                4-stage explanation, before SATE. FOH-
                                toggled-off notes are filtered out backend-
                                side, so this list is always the customer-
                                visible subset. */}
                            {(item.customer_notes || []).length > 0 && (
                              <div className="space-y-2 mb-3" data-testid={`portal-customer-notes-${item.id}`}>
                                {item.customer_notes.map((note) => (
                                  <div
                                    key={note.id || note.key}
                                    className={`flex items-start gap-2 p-2.5 rounded-lg border ${
                                      isDangerous
                                        ? 'bg-slate-700/60 border-slate-600'
                                        : 'bg-amber-50 border-amber-200'
                                    }`}
                                    data-testid={`portal-customer-note-${note.key}`}
                                  >
                                    <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                                      isDangerous ? 'text-amber-300' : 'text-amber-600'
                                    }`} />
                                    <p className={`text-xs leading-relaxed ${
                                      isDangerous ? 'text-amber-100' : 'text-amber-900'
                                    }`}>
                                      <strong>Please note: </strong>{note.text}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* SATE — Layer 2 workflow/process card.
                                Renders between the deterministic
                                4-stage findings and the Important Note. */}
                            <SATEBlock sate={item.sate} dark={isDangerous} />

                            {/* Phase-5 MCC — "subject to confirmation" tag.
                                Surfaces a gentle disclaimer when the tester
                                picked a non-price_now repair route. Never
                                discloses the internal route value. */}
                            {item.subject_to_inspection && (
                              <div
                                className={`flex items-start gap-2 p-2.5 rounded-lg border mt-2 ${
                                  isDangerous
                                    ? 'bg-slate-700/60 border-slate-600 text-slate-100'
                                    : 'bg-slate-50 border-slate-300 text-slate-700'
                                }`}
                                data-testid={`portal-subject-to-inspection-${item.id}`}
                              >
                                <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDangerous ? 'text-slate-300' : 'text-slate-600'}`} />
                                <p className="text-[12.5px] leading-snug">
                                  <span className="font-semibold">Final price subject to a brief inspection.</span>{' '}
                                  Once we have the vehicle in, we'll confirm the final
                                  quote — the price shown is our best estimate from the
                                  MOT result.
                                </p>
                              </div>
                            )}

                            {/* iter197m — High-visibility "*Important
                                Note" (red item path). Bumped from
                                subtle slate to amber-bordered + ring
                                + bold so customers can't miss it. */}
                            {item.contextual_customer_note?.text && (
                              <div
                                className={`mt-2 mb-3 flex items-start gap-2.5 p-3 rounded-lg border-2 ring-1 shadow-sm ${
                                  isDangerous
                                    ? 'bg-amber-950/40 border-amber-500/70 ring-amber-500/30'
                                    : 'bg-amber-50 border-amber-400 ring-amber-200'
                                }`}
                                data-testid={`portal-contextual-note-${item.id}`}
                              >
                                <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                                  isDangerous ? 'text-amber-300' : 'text-amber-700'
                                }`} />
                                <div className="min-w-0">
                                  <p className={`text-xs uppercase tracking-wide font-bold mb-1 ${
                                    isDangerous ? 'text-amber-200' : 'text-amber-900'
                                  }`}>
                                    *Important Note
                                  </p>
                                  <p className={`text-sm leading-relaxed font-medium ${
                                    isDangerous ? 'text-amber-50' : 'text-amber-950'
                                  }`}>
                                    {item.contextual_customer_note.text}
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Photos */}
                            {item.photos?.length > 0 && (
                              <div className="mb-3">
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                  {item.photos.slice(0, 3).map((photo, idx) => (
                                    <img
                                      key={idx}
                                      src={photo}
                                      alt={`Evidence ${idx + 1}`}
                                      className="w-14 h-14 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openPhotoViewer(photo, item.photos);
                                      }}
                                    />
                                  ))}
                                  {item.photos.length > 3 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openPhotoViewer(item.photos[3], item.photos);
                                      }}
                                      className={`text-xs self-center font-semibold underline ${isDangerous ? 'text-slate-300' : 'text-slate-600'}`}
                                    >
                                      +{item.photos.length - 3} more
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="flex flex-col gap-2">
                              {declinedItems[item.id] ? (
                                // Item is declined — show reason + objection response
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between bg-slate-100 rounded-lg px-3 py-2">
                                    <span className="text-xs text-slate-600">
                                      Skipped: <strong>{DECLINE_REASONS[declinedItems[item.id]]?.label}</strong>
                                    </span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleUndoDecline(item.id); }}
                                      className="text-xs text-blue-600 hover:underline"
                                      data-testid={`undo-decline-${item.id}`}
                                    >
                                      Change mind
                                    </button>
                                  </div>
                                  <div className={`p-3 rounded-xl border text-sm leading-relaxed ${
                                    isDangerous ? 'bg-yellow-900/20 border-yellow-600/30 text-yellow-100' : 'bg-blue-50 border-blue-200 text-slate-700'
                                  }`} data-testid={`objection-response-${item.id}`}>
                                    {DECLINE_REASONS[declinedItems[item.id]]?.response}
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleApproveItem(item.id, e.currentTarget); handleUndoDecline(item.id); }}
                                    className="w-full py-2.5 px-3 rounded-lg font-semibold text-sm transition-all bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm"
                                    data-testid={`reconsider-approve-${item.id}`}
                                  >
                                    I'd like to go ahead with this
                                  </button>
                                  {/* iter211m — Explicit "keep skipped" confirmation so the
                                      customer knows they can move on. The item state is
                                      already saved (declinedItems has the reason); this
                                      button just collapses the objection-response panel
                                      so the card reads as a clean skipped-line and the
                                      customer can proceed to the confirm gate. */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedSummaryItems(prev => ({ ...prev, [item.id]: false }));
                                    }}
                                    className={`w-full py-2 px-3 rounded-lg font-medium text-xs border transition-all ${
                                      isDangerous
                                        ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                        : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                                    }`}
                                    data-testid={`confirm-decline-${item.id}`}
                                  >
                                    Keep it skipped — continue
                                  </button>
                                </div>
                              ) : showDeclineDropdown[item.id] ? (
                                // Showing decline reason selector
                                <div className="space-y-2" data-testid={`decline-reasons-${item.id}`}>
                                  <p className={`text-xs font-medium ${isDangerous ? 'text-slate-300' : 'text-slate-600'}`}>
                                    Can you tell us why? This helps us understand and find a solution:
                                  </p>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {Object.entries(DECLINE_REASONS).map(([key, { label }]) => (
                                      <button
                                        key={key}
                                        onClick={(e) => { e.stopPropagation(); handleSelectDeclineReason(item.id, key); }}
                                        className={`text-xs py-2 px-2 rounded-lg border text-left transition-all ${
                                          isDangerous
                                            ? 'border-slate-600 text-slate-300 hover:bg-slate-700 hover:border-slate-500'
                                            : 'border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                                        }`}
                                        data-testid={`decline-reason-${key}-${item.id}`}
                                      >
                                        {label}
                                      </button>
                                    ))}
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setShowDeclineDropdown(prev => ({ ...prev, [item.id]: false })); }}
                                    className={`w-full text-xs py-1.5 ${isDangerous ? 'text-slate-400' : 'text-slate-500'} hover:underline`}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : !isApproved ? (
                                // Default: Approve / Decline buttons
                                // iter197ii — Match the advisory item's
                                // button proportions: full-width green
                                // Approve + small outlined Decline.
                                // Same sizing/colour everywhere in the
                                // portal for visual consistency.
                                // iter198q (Feb 2026) — Approve button
                                // styled as OUTLINED (not solid) until
                                // the customer clicks it, so the
                                // unapproved state is unmistakably an
                                // *action* rather than a state. Once
                                // approved the button flips to solid
                                // green ("Approved — tap to change") in
                                // the isApproved branch below.
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApproveItem(item.id, e.currentTarget);
                                    }}
                                    className="flex-1 py-2.5 px-3 rounded-lg font-semibold text-sm transition-all bg-white border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600 shadow-sm"
                                    data-testid={`summary-approve-${item.id}`}
                                  >
                                    Approve repair
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeclineItem(item.id);
                                    }}
                                    className="py-2.5 px-3 rounded-lg font-medium text-sm border-2 border-red-200 bg-red-50/60 text-red-700 hover:bg-red-100 hover:border-red-300 transition-all"
                                    data-testid={`summary-decline-${item.id}`}
                                  >
                                    Skip for now
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDecideLater(item.id);
                                  }}
                                  className="w-full py-2.5 px-3 rounded-lg font-medium text-sm border border-green-300 text-green-700 bg-green-50 hover:bg-green-100 transition-all"
                                >
                                  Approved — tap to change
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Recommended Items - Expandable Box */}
          {recommendedItems.length > 0 && (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 overflow-hidden">
              {/* Clickable Header */}
              <button
                onClick={() => setRecommendedSummaryExpanded(!recommendedSummaryExpanded)}
                className="w-full flex items-center justify-between p-3 hover:bg-amber-100 transition-colors"
                data-testid="recommended-summary-toggle"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                  <div className="text-left">
                    <span className="block text-amber-900 font-semibold leading-tight">
                      Recommended Attention
                    </span>
                    <span className="block text-[11.5px] text-amber-700 leading-tight mt-0.5">
                      {recommendedItems.length} {recommendedItems.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-amber-900">£{recommendedTotal.toFixed(2)}</span>
                  {recommendedSummaryExpanded ? (
                    <ChevronDown className="w-5 h-5 text-amber-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-amber-600" />
                  )}
                </div>
              </button>
              
              {/* Expanded Content - Item Details (Level 2 Accordion) */}
              {recommendedSummaryExpanded && (
                <div className="border-t border-amber-200 bg-amber-50/50 p-3 space-y-2">
                  {_visibleRecommendedItems.map(item => {
                    const isExpanded = expandedSummaryItems[item.id];
                    const isApproved = approvedItems.has(item.id);
                    // iter198z (Feb 2026) — Visual feedback on approval state.
                    // Approved items render in green, declined items in red.
                    const isDeclined = !isApproved && !!declinedItems?.[item.id];
                    const aiExplanation = aiExplanations[item.id];

                    // Iter187 — structured tyre group card (amber-only path).
                    if (item.tyre_group_subtype === 'tyre_replacement_group') {
                      const _touchAll = (
                        selectedOption === 'all'
                        || (selectedOption === 'essential'
                            && (item.is_essential_safety || perTyreDeclines[item.id]?.length))
                      );
                      const _optionKey = selectedOption || 'manual';
                      return (
                        <TyrePortalCard
                          key={`${item.id}::opt=${_optionKey}::touchAll=${_touchAll ? 'T' : 'F'}`}
                          item={item}
                          isApproved={isApproved}
                          isDangerous={false}
                          onPerTyreDecisionChange={handlePerTyreDecisionChange}
                          forceDeclinedPositions={perTyreDeclines[item.id]}
                          forceApprovedPositions={perTyreApproved[item.id]}
                          forceTouchAll={_touchAll}
                        />
                      );
                    }
                    
                    // iter198l (Feb 2026) — Visual differentiation
                    // for "untestable" advisories (DVSA "cannot
                    // check / unable to test"). Blue tint + "Info
                    // only" badge so the customer can spot at a
                    // glance that this is verification advice — no
                    // billable repair.
                    const isUntestable = !!(
                      item.untestable_advisory
                      || item.explanation?.untestable_advisory
                      || item.source_type === "seatbelt_unable_obstructed"
                    );

                    return (
                      <div 
                        key={item.id}
                        className={`rounded-xl overflow-hidden transition-all ring-1 ring-transparent ${
                          isDeclined
                            ? "bg-rose-50 border border-rose-300 ring-rose-200"
                            : isApproved
                            ? "bg-emerald-50 border border-emerald-300 ring-emerald-200"
                            : isUntestable
                            ? "bg-white border border-blue-200"
                            : "bg-white border border-amber-200"
                        }`}
                        data-testid={`portal-item-card-${item.id}`}
                        data-approval-state={isApproved ? 'approved' : isDeclined ? 'declined' : 'pending'}
                      >
                        {/* Clickable Header Row */}
                        <button
                          onClick={() => toggleSummaryItemExpanded(item.id)}
                          className={`w-full p-3 flex items-center justify-between transition-opacity ${
                            isApproved ? "hover:bg-emerald-100" :
                            isDeclined ? "hover:bg-rose-100" :
                            isUntestable ? "hover:bg-blue-50" : "hover:bg-amber-50"
                          }`}
                          data-testid={`summary-item-toggle-${item.id}`}
                        >
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-slate-900">{item.label}</span>
                              {isUntestable && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold uppercase tracking-wide"
                                  data-testid={`untestable-info-badge-${item.id}`}
                                  title="The tester could not verify this item. No repair work required."
                                >
                                  <Info className="w-3 h-3" />
                                  Info only
                                </span>
                              )}
                              {isApproved && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wide"
                                  data-testid={`portal-item-approved-badge-${item.id}`}
                                >
                                  <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                                  Approved
                                </span>
                              )}
                              {isDeclined && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wide"
                                  data-testid={`portal-item-declined-badge-${item.id}`}
                                >
                                  <XCircle className="w-3 h-3" strokeWidth={2.5} />
                                  Skipped
                                </span>
                              )}
                            </div>
                            {!isExpanded && (
                              <p className="text-xs mt-1 text-slate-500">
                                Tap for details
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* iter197p — Info-only JGs (FOH wrote a
                                customer note but isn't quoting work)
                                show a "Note" chip instead of £0.
                                iter198m — Untestable advisories show
                                a "No charge" chip — there's nothing
                                to authorise or decline. */}
                            {isUntestable ? (
                              <span
                                className="text-[10px] font-bold uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded"
                                data-testid={`portal-untestable-no-charge-${item.id}`}
                              >
                                No charge
                              </span>
                            ) : item.info_only ? (
                              <span
                                className="text-[10px] font-bold uppercase tracking-wide bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded"
                                data-testid={`portal-info-only-chip-${item.id}`}
                              >
                                Note
                              </span>
                            ) : (
                              <span className={`font-bold text-sm ${
                                isApproved ? 'text-emerald-700'
                                : isDeclined ? 'text-slate-400 line-through'
                                : 'text-amber-700'
                              }`}>
                                £{(item.price_inc_vat || 0).toFixed(2)}
                              </span>
                            )}
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-amber-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-amber-500" />
                            )}
                          </div>
                        </button>
                        
                        {/* Expanded Content - Full Card Details */}
                        {isExpanded && (
                          <div className="px-3 pb-3 pt-2 border-t border-amber-100 bg-amber-50/30">
                            {/* iter197uu — Customer Advice Engine (CAE)
                                narrative for amber/recommended items.
                                Previously the CAE summary was only
                                rendered on the dangerous/red path —
                                FOH-generated advice on a recommended
                                item (e.g. "Four Wheel Alignment —
                                Preventative Maintenance") never made
                                it to the customer portal. Mirrors the
                                red path block at line ~1713. */}
                            {/* 4-SECTION CUSTOMER EXPLANATION — iter211d:
                                when CAE summary exists, the 4-section
                                block is duplicate noise. Hide it behind
                                a "Technical detail" toggle so the CAE
                                paragraph is the primary voice. */}
                            {item.explanation?.has_structured_explanation ? (
                              item.customer_advice?.summary ? (
                                <details
                                  className="mb-3"
                                  data-testid={`structured-explanation-toggle-${item.id}`}
                                >
                                  <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700 select-none py-1">
                                    Technical detail
                                  </summary>
                                  <div className="space-y-3 mt-2" data-testid={`structured-explanation-${item.id}`}>
                                    <div>
                                      <p className="text-xs font-bold uppercase tracking-wider mb-1 text-slate-500">What we found</p>
                                      <p className="text-sm leading-relaxed text-slate-700">{item.explanation.what_found || item.notes}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs font-bold uppercase tracking-wider mb-1 text-amber-600">What this means</p>
                                      <p className="text-sm leading-relaxed text-slate-700">{item.explanation.what_means}</p>
                                    </div>
                                    <div className="p-2.5 rounded-lg bg-amber-100/60">
                                      <p className="text-xs font-bold uppercase tracking-wider mb-1 text-amber-800">What's required</p>
                                      <p className="text-sm leading-relaxed font-medium text-slate-800">{item.explanation.whats_required || item.explanation.what_required}</p>
                                    </div>
                                    {(item.explanation.consequence || item.explanation.consequences) && (
                                      <div className="p-2.5 rounded-lg border bg-orange-50 border-orange-200">
                                        <p className="text-xs font-bold uppercase tracking-wider mb-1 text-orange-700">What happens if not addressed</p>
                                        <p className="text-sm leading-relaxed text-orange-800">{item.explanation.consequence || item.explanation.consequences}</p>
                                      </div>
                                    )}
                                  </div>
                                </details>
                              ) : (
                              <div className="space-y-3 mb-3" data-testid={`structured-explanation-${item.id}`}>
                                {/* WHAT WE FOUND */}
                                <div>
                                  <p className="text-xs font-bold uppercase tracking-wider mb-1 text-slate-500">
                                    What we found
                                  </p>
                                  <p className="text-sm leading-relaxed text-slate-700">
                                    {item.explanation.what_found || item.notes}
                                  </p>
                                </div>
                                
                                {/* WHAT THIS MEANS */}
                                <div>
                                  <p className="text-xs font-bold uppercase tracking-wider mb-1 text-amber-600">
                                    What this means
                                  </p>
                                  <p className="text-sm leading-relaxed text-slate-700">
                                    {item.explanation.what_means}
                                  </p>
                                </div>
                                
                                {/* WHAT'S REQUIRED */}
                                <div className="p-2.5 rounded-lg bg-amber-100/60">
                                  <p className="text-xs font-bold uppercase tracking-wider mb-1 text-amber-800">
                                    What's required
                                  </p>
                                  <p className="text-sm leading-relaxed font-medium text-slate-800">
                                    {item.explanation.whats_required || item.explanation.what_required}
                                  </p>
                                </div>
                                
                                {/* WHAT HAPPENS IF NOT ADDRESSED — only for REPLACE_SOON / RED items, collapsible for MONITOR */}
                                {(item.explanation.consequence || item.explanation.consequences) && (
                                  item.pad_sub_status === 'MONITOR' ? (
                                    <details className="p-2.5 rounded-lg border bg-orange-50/50 border-orange-100">
                                      <summary className="text-xs font-bold uppercase tracking-wider text-orange-600 cursor-pointer select-none">
                                        What happens if not addressed
                                      </summary>
                                      <p className="text-sm leading-relaxed text-orange-700 mt-2">
                                        {item.explanation.consequence || item.explanation.consequences}
                                      </p>
                                    </details>
                                  ) : (
                                    <div className="p-2.5 rounded-lg border bg-orange-50 border-orange-200">
                                      <p className="text-xs font-bold uppercase tracking-wider mb-1 text-orange-700">
                                        What happens if not addressed
                                      </p>
                                      <p className="text-sm leading-relaxed text-orange-800">
                                        {item.explanation.consequence || item.explanation.consequences}
                                      </p>
                                    </div>
                                  )
                                )}
                              </div>
                              )
                            ) : (
                              <div className="space-y-2 mb-3">
                                <p className="text-sm text-amber-700">
                                  {item.notes || 'We recommend addressing this to prevent bigger issues later.'}
                                </p>
                              </div>
                            )}

                            {/* iter198w (Feb 2026) — Customer advice
                                paragraph callout — amber path mirror of
                                the red-path block above. Sits BELOW the
                                structured 4-stage explanation so the
                                customer first reads the evidence-led
                                summary, then a personalised "more from
                                your service advisor" note. */}
                            {item.customer_advice?.summary && (
                              <div
                                className="space-y-2 mb-3 p-3 rounded-lg border-l-4 bg-indigo-50/70 border-indigo-400"
                                data-testid={`cae-portal-amber-${item.id}`}
                              >
                                <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-700">
                                  More from your service advisor
                                </p>
                                <p
                                  className="text-sm leading-relaxed text-slate-800"
                                  data-testid={`cae-portal-amber-summary-${item.id}`}
                                >
                                  {item.customer_advice.summary}
                                </p>
                                {item.customer_advice.expanded && (
                                  <details
                                    className="text-xs text-slate-600"
                                    data-testid={`cae-portal-amber-expanded-toggle-${item.id}`}
                                  >
                                    <summary className="cursor-pointer font-medium text-indigo-700 hover:text-indigo-900">
                                      Read more
                                    </summary>
                                    <p className="mt-2 leading-relaxed" data-testid={`cae-portal-amber-expanded-${item.id}`}>
                                      {item.customer_advice.expanded}
                                    </p>
                                  </details>
                                )}
                              </div>
                            )}

                            {/* iter197ff — Standard advisories (amber path).
                                Same fix as the red/dangerous path: render
                                FOH-curated advisory notes on the customer
                                portal so the customer actually sees them. */}
                            {(item.customer_notes || []).length > 0 && (
                              <div className="space-y-2 mb-3" data-testid={`portal-customer-notes-${item.id}`}>
                                {item.customer_notes.map((note) => (
                                  <div
                                    key={note.id || note.key}
                                    className="flex items-start gap-2 p-2.5 rounded-lg border bg-amber-50 border-amber-200"
                                    data-testid={`portal-customer-note-${note.key}`}
                                  >
                                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                                    <p className="text-xs leading-relaxed text-amber-900">
                                      <strong>Please note: </strong>{note.text}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* SATE — Layer 2 workflow/process card
                                (amber path). */}
                            <SATEBlock sate={item.sate} />

                            {/* Phase-5 MCC — "subject to confirmation" tag (amber items). */}
                            {item.subject_to_inspection && (
                              <div
                                className="flex items-start gap-2 p-2.5 rounded-lg border mt-2 bg-slate-50 border-slate-300 text-slate-700"
                                data-testid={`portal-subject-to-inspection-${item.id}`}
                              >
                                <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-600" />
                                <p className="text-[12.5px] leading-snug">
                                  <span className="font-semibold">Final price subject to a brief inspection.</span>{' '}
                                  Once we have the vehicle in, we'll confirm the final
                                  quote — the price shown is our best estimate from the
                                  MOT result.
                                </p>
                              </div>
                            )}

                            {/* iter197m — High-visibility "*Important
                                Note" (amber item path). Same amber-
                                bordered + ring + bold treatment as the
                                red and primary paths so customers can't
                                miss it. */}
                            {item.contextual_customer_note?.text && (
                              <div
                                className="mt-2 mb-3 flex items-start gap-2.5 p-3 rounded-lg border-2 ring-1 shadow-sm bg-amber-50 border-amber-400 ring-amber-200"
                                data-testid={`portal-contextual-note-${item.id}`}
                              >
                                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-700" />
                                <div className="min-w-0">
                                  <p className="text-xs uppercase tracking-wide font-bold mb-1 text-amber-900">
                                    *Important Note
                                  </p>
                                  <p className="text-sm leading-relaxed font-medium text-amber-950">
                                    {item.contextual_customer_note.text}
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {/* Photos */}
                            {item.photos?.length > 0 && (
                              <div className="mb-3">
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                  {item.photos.slice(0, 3).map((photo, idx) => (
                                    <img
                                      key={idx}
                                      src={photo}
                                      alt={`Evidence ${idx + 1}`}
                                      className="w-14 h-14 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openPhotoViewer(photo, item.photos);
                                      }}
                                    />
                                  ))}
                                  {item.photos.length > 3 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openPhotoViewer(item.photos[3], item.photos);
                                      }}
                                      className="text-xs self-center text-slate-600 font-semibold underline"
                                    >
                                      +{item.photos.length - 3} more
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="flex flex-col gap-2">
                              {isUntestable ? (
                                /* iter198m (Feb 2026) — Untestable
                                   advisories have no repair to
                                   authorise or decline. Replace the
                                   3-button trio with a single
                                   acknowledgement so the framing
                                   matches the no-action-required
                                   reality. Reuses `approvedItems` so
                                   the existing collapse + summary
                                   plumbing works unchanged. */
                                !isApproved ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApproveItem(item.id);
                                    }}
                                    className="w-full py-2.5 px-3 rounded-lg font-semibold text-sm transition-all bg-blue-500 hover:bg-blue-600 text-white shadow-sm"
                                    data-testid={`summary-acknowledge-${item.id}`}
                                  >
                                    Got it, thanks
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDecideLater(item.id);
                                    }}
                                    className="w-full py-2.5 px-3 rounded-lg font-medium text-sm border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-all"
                                    data-testid={`summary-acknowledged-${item.id}`}
                                  >
                                    Acknowledged — tap to undo
                                  </button>
                                )
                              ) : item.info_only ? (
                                /* iter197p — Info-only row: no approve /
                                   decline UI. The CCN is the whole
                                   message; FOH isn't quoting work, so
                                   there's nothing for the customer to
                                   action. Renders a quiet pill so it's
                                   clear the row is informational. */
                                <div
                                  className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 italic"
                                  data-testid={`info-only-footer-${item.id}`}
                                >
                                  Your service advisor has added a note about this item — no action required.
                                </div>
                              ) : (
                                declinedItems[item.id] ? (
                                // Item is declined — show reason + objection response
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between bg-slate-100 rounded-lg px-3 py-2">
                                    <span className="text-xs text-slate-600">
                                      Skipped: <strong>{DECLINE_REASONS[declinedItems[item.id]]?.label}</strong>
                                    </span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleUndoDecline(item.id); }}
                                      className="text-xs text-blue-600 hover:underline"
                                    >
                                      Change mind
                                    </button>
                                  </div>
                                  <div className="p-3 rounded-xl border bg-blue-50 border-blue-200 text-sm leading-relaxed text-slate-700"
                                    data-testid={`objection-response-${item.id}`}
                                  >
                                    {DECLINE_REASONS[declinedItems[item.id]]?.response}
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleApproveItem(item.id, e.currentTarget); handleUndoDecline(item.id); }}
                                    className="w-full py-2.5 px-3 rounded-lg font-semibold text-sm transition-all bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm"
                                  >
                                    I'd like to go ahead with this
                                  </button>
                                  {/* iter211m — Explicit "keep skipped" confirmation so the
                                      customer knows they can move on. */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedSummaryItems(prev => ({ ...prev, [item.id]: false }));
                                    }}
                                    className="w-full py-2 px-3 rounded-lg font-medium text-xs border border-slate-300 text-slate-600 hover:bg-slate-50 transition-all"
                                    data-testid={`confirm-decline-${item.id}`}
                                  >
                                    Keep it skipped — continue
                                  </button>
                                </div>
                              ) : showDeclineDropdown[item.id] ? (
                                // Showing decline reason selector
                                <div className="space-y-2" data-testid={`decline-reasons-${item.id}`}>
                                  <p className="text-xs font-medium text-slate-600">
                                    Can you tell us why? This helps us understand and find a solution:
                                  </p>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {Object.entries(DECLINE_REASONS).map(([key, { label }]) => (
                                      <button
                                        key={key}
                                        onClick={(e) => { e.stopPropagation(); handleSelectDeclineReason(item.id, key); }}
                                        className="text-xs py-2 px-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 text-left transition-all"
                                      >
                                        {label}
                                      </button>
                                    ))}
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setShowDeclineDropdown(prev => ({ ...prev, [item.id]: false })); }}
                                    className="w-full text-xs py-1.5 text-slate-500 hover:underline"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : !isApproved ? (
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApproveItem(item.id, e.currentTarget);
                                    }}
                                    className="flex-1 py-2.5 px-3 rounded-lg font-semibold text-sm transition-all bg-white border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600 shadow-sm"
                                    data-testid={`summary-approve-${item.id}`}
                                  >
                                    Approve repair
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeclineItem(item.id);
                                    }}
                                    className="py-2.5 px-3 rounded-lg font-medium text-sm border-2 border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-all"
                                    data-testid={`summary-decline-${item.id}`}
                                  >
                                    Skip for now
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDecideLater(item.id);
                                  }}
                                  className="w-full py-2.5 px-3 rounded-lg font-medium text-sm border border-green-300 text-green-700 bg-green-50 hover:bg-green-100 transition-all"
                                >
                                  Approved — tap to change
                                </button>
                              )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Total — iter211n itemised breakdown. Previously the block
              only showed the £grandTotal number, so customers saw
              "£499.83 urgent + £194.16 recommended = mental sum £693.99"
              but a grand total of £774.79 with an unexplained £80.80
              gap (charges £22.20 + already-booked MOT £58.60 baked in
              silently). Now we surface every component line so the
              math is fully transparent and the customer knows exactly
              what they'd be authorising. */}
          <div className="pt-3 mt-3 border-t border-slate-200 space-y-1.5" data-testid="portal-grand-total-breakdown">
            {urgentTotal > 0 && (
              <div className="flex items-center justify-between text-xs text-slate-500" data-testid="grand-total-line-urgent">
                <span>Urgent Attention ({urgentItems.length} {urgentItems.length === 1 ? 'item' : 'items'})</span>
                <span>£{urgentTotal.toFixed(2)}</span>
              </div>
            )}
            {recommendedTotal > 0 && (
              <div className="flex items-center justify-between text-xs text-slate-500" data-testid="grand-total-line-recommended">
                <span>Recommended Attention ({recommendedItems.length} {recommendedItems.length === 1 ? 'item' : 'items'})</span>
                <span>£{recommendedTotal.toFixed(2)}</span>
              </div>
            )}
            {chargesTotalIncVat > 0 && (
              <div className="flex items-center justify-between text-xs text-slate-500" data-testid="grand-total-line-charges">
                <span>Workshop consumables &amp; environmental</span>
                <span>£{chargesTotalIncVat.toFixed(2)}</span>
              </div>
            )}
            {alreadyAuthorisedValue > 0 && (
              <div className="flex items-center justify-between text-xs text-slate-500" data-testid="grand-total-line-already-agreed">
                <span>{data?.intent_config?.already_authorised_label || 'Already booked'} (already authorised)</span>
                <span>£{alreadyAuthorisedValue.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-200">
              <span className="text-slate-700 font-medium">If all recommended work is completed:</span>
              <span className="text-lg font-bold text-slate-900" data-testid="portal-grand-total">£{grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>


      {/* ============== VEHICLE VITALS (Like Hospital Vital Signs) ============== */}
      {data?.health_indicators && (
        <div className="px-4 py-5 border-t border-slate-200">
          <button
            onClick={() => setShowHealthReport(!showHealthReport)}
            className="w-full flex items-center justify-between py-4 px-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
            data-testid="health-report-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <span className="font-semibold text-slate-800 block">Your Vehicle Health Report</span>
                <span className="text-xs text-slate-500">Battery, brakes, tyres & fluids condition</span>
              </div>
            </div>
            {showHealthReport ? (
              <ChevronDown className="w-5 h-5 text-blue-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-blue-500" />
            )}
          </button>
          
          {showHealthReport && (
            <div className="mt-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
              
              {/* ========== BATTERY HEALTH ========== */}
              {data.health_indicators.battery && (
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Battery className={`w-5 h-5 ${
                        data.health_indicators.battery.health_percent > 50 ? 'text-emerald-600' :
                        data.health_indicators.battery.health_percent > 25 ? 'text-amber-600' : 'text-red-600'
                      }`} />
                      <span className="font-semibold text-slate-800">Battery</span>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      data.health_indicators.battery.health_percent > 50 ? 'bg-emerald-100 text-emerald-700' :
                      data.health_indicators.battery.health_percent > 25 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {data.health_indicators.battery.health_percent > 50 ? 'Good' :
                       data.health_indicators.battery.health_percent > 25 ? 'Monitor' : 'Replace Soon'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Circular Gauge */}
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                        <circle className="text-slate-200" strokeWidth="3" stroke="currentColor" fill="transparent" r="15" cx="18" cy="18" />
                        <circle
                          className={data.health_indicators.battery.health_percent > 50 ? 'text-emerald-500' : data.health_indicators.battery.health_percent > 25 ? 'text-amber-500' : 'text-red-500'}
                          strokeWidth="3" strokeLinecap="round" stroke="currentColor" fill="transparent" r="15" cx="18" cy="18"
                          strokeDasharray={`${(data.health_indicators.battery.health_percent || 0) * 0.94} 100`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`text-lg font-bold ${
                          data.health_indicators.battery.health_percent > 50 ? 'text-emerald-700' : 
                          data.health_indicators.battery.health_percent > 25 ? 'text-amber-700' : 'text-red-700'
                        }`}>
                          {Math.round(data.health_indicators.battery.health_percent)}%
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-600 mb-1">
                        <strong>Life Remaining:</strong> {Math.round(data.health_indicators.battery.health_percent)}%
                      </p>
                      {data.health_indicators.battery.voltage_v && (
                        <p className="text-xs text-slate-500">Voltage: {data.health_indicators.battery.voltage_v}V</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        {data.health_indicators.battery.health_percent > 50 
                          ? 'Battery is in good condition' 
                          : data.health_indicators.battery.health_percent > 25 
                            ? 'Battery showing wear - keep an eye on it'
                            : 'Battery is weak - replacement recommended'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* ========== BRAKE FLUID ========== */}
              {data.health_indicators.brake_fluid && (
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Droplets className={`w-5 h-5 ${
                        data.health_indicators.brake_fluid.health_percent > 50 ? 'text-emerald-600' :
                        data.health_indicators.brake_fluid.health_percent > 25 ? 'text-amber-600' : 'text-red-600'
                      }`} />
                      <span className="font-semibold text-slate-800">Brake Fluid</span>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      data.health_indicators.brake_fluid.health_percent > 50 ? 'bg-emerald-100 text-emerald-700' :
                      data.health_indicators.brake_fluid.health_percent > 25 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {data.health_indicators.brake_fluid.health_percent > 50 ? 'Good' :
                       data.health_indicators.brake_fluid.health_percent > 25 ? 'Due Soon' : 'Replace Now'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                        <circle className="text-slate-200" strokeWidth="3" stroke="currentColor" fill="transparent" r="15" cx="18" cy="18" />
                        <circle
                          className={data.health_indicators.brake_fluid.health_percent > 50 ? 'text-emerald-500' : data.health_indicators.brake_fluid.health_percent > 25 ? 'text-amber-500' : 'text-red-500'}
                          strokeWidth="3" strokeLinecap="round" stroke="currentColor" fill="transparent" r="15" cx="18" cy="18"
                          strokeDasharray={`${(data.health_indicators.brake_fluid.health_percent || 0) * 0.94} 100`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`text-lg font-bold ${
                          data.health_indicators.brake_fluid.health_percent > 50 ? 'text-emerald-700' : 
                          data.health_indicators.brake_fluid.health_percent > 25 ? 'text-amber-700' : 'text-red-700'
                        }`}>
                          {Math.round(data.health_indicators.brake_fluid.health_percent)}%
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-600 mb-1">
                        <strong>Life Remaining:</strong> {Math.round(data.health_indicators.brake_fluid.health_percent)}%
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {data.health_indicators.brake_fluid.health_percent > 50 
                          ? 'Brake fluid is fresh and effective' 
                          : data.health_indicators.brake_fluid.health_percent > 25 
                            ? 'Brake fluid absorbing moisture - change soon'
                            : 'Brake fluid degraded - safety critical, change now'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* ========== COOLANT ========== */}
              {data.health_indicators.coolant && (
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Thermometer className={`w-5 h-5 ${
                        data.health_indicators.coolant.health_percent > 50 ? 'text-emerald-600' :
                        data.health_indicators.coolant.health_percent > 25 ? 'text-amber-600' : 'text-red-600'
                      }`} />
                      <span className="font-semibold text-slate-800">Coolant</span>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      data.health_indicators.coolant.health_percent > 50 ? 'bg-emerald-100 text-emerald-700' :
                      data.health_indicators.coolant.health_percent > 25 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {data.health_indicators.coolant.health_percent > 50 ? 'Good' :
                       data.health_indicators.coolant.health_percent > 25 ? 'Monitor' : 'Replace'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                        <circle className="text-slate-200" strokeWidth="3" stroke="currentColor" fill="transparent" r="15" cx="18" cy="18" />
                        <circle
                          className={data.health_indicators.coolant.health_percent > 50 ? 'text-emerald-500' : data.health_indicators.coolant.health_percent > 25 ? 'text-amber-500' : 'text-red-500'}
                          strokeWidth="3" strokeLinecap="round" stroke="currentColor" fill="transparent" r="15" cx="18" cy="18"
                          strokeDasharray={`${(data.health_indicators.coolant.health_percent || 0) * 0.94} 100`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`text-lg font-bold ${
                          data.health_indicators.coolant.health_percent > 50 ? 'text-emerald-700' : 
                          data.health_indicators.coolant.health_percent > 25 ? 'text-amber-700' : 'text-red-700'
                        }`}>
                          {Math.round(data.health_indicators.coolant.health_percent)}%
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-600 mb-1">
                        <strong>Life Remaining:</strong> {Math.round(data.health_indicators.coolant.health_percent)}%
                      </p>
                      {data.health_indicators.coolant.measurement_value && (
                        <p className="text-sm text-slate-600 mb-1">
                          <strong>Freeze Protection:</strong> {data.health_indicators.coolant.measurement_value}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        {data.health_indicators.coolant.health_percent > 50 
                          ? 'Coolant protecting your engine well' 
                          : data.health_indicators.coolant.health_percent > 25 
                            ? 'Coolant losing effectiveness'
                            : 'Coolant needs replacing to protect engine'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* ========== TYRE TREAD - All 4 Tyres ========== */}
              {data.health_indicators.tyres && (
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center gap-2 mb-4">
                    <Car className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold text-slate-800">Tyre Tread Depth</span>
                  </div>
                  
                  {/* Car diagram with 4 tyres */}
                  <div className="relative bg-slate-50 rounded-xl p-4">
                    {/* Visual car shape */}
                    <div className="flex justify-center mb-2">
                      <span className="text-xs text-slate-400 uppercase tracking-wider">Front</span>
                    </div>
                    
                    {/* Front tyres */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      {[
                        { pos: 'fl', label: 'Front Left', val: data.health_indicators.tyres.fl },
                        { pos: 'fr', label: 'Front Right', val: data.health_indicators.tyres.fr }
                      ].map(({ pos, label, val }) => {
                        const depth = parseFloat(val) || 0;
                        const lifePercent = Math.min(100, Math.max(0, ((depth - 1.6) / (8 - 1.6)) * 100));
                        const isGood = depth > 3;
                        const isWarning = depth > 1.6 && depth <= 3;
                        const isCritical = depth <= 1.6;
                        
                        return (
                          <div key={pos} className={`rounded-xl p-3 border-2 ${
                            isCritical ? 'bg-red-50 border-red-300' :
                            isWarning ? 'bg-amber-50 border-amber-300' :
                            'bg-emerald-50 border-emerald-300'
                          }`}>
                            <div className="text-center">
                              <div className={`text-2xl font-bold ${
                                isCritical ? 'text-red-700' : isWarning ? 'text-amber-700' : 'text-emerald-700'
                              }`}>{depth || '?'}mm</div>
                              <div className="text-xs text-slate-500 mt-1">{label}</div>
                              {/* Mini progress bar */}
                              <div className="h-1.5 bg-white/70 rounded-full overflow-hidden mt-2">
                                <div 
                                  className={`h-full rounded-full ${
                                    isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500'
                                  }`}
                                  style={{ width: `${lifePercent}%` }}
                                />
                              </div>
                              <div className={`text-xs font-semibold mt-1 ${
                                isCritical ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-emerald-600'
                              }`}>
                                {isCritical ? 'REPLACE' : isWarning ? 'MONITOR' : 'OK'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Rear tyres */}
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { pos: 'rl', label: 'Rear Left', val: data.health_indicators.tyres.rl },
                        { pos: 'rr', label: 'Rear Right', val: data.health_indicators.tyres.rr }
                      ].map(({ pos, label, val }) => {
                        const depth = parseFloat(val) || 0;
                        const lifePercent = Math.min(100, Math.max(0, ((depth - 1.6) / (8 - 1.6)) * 100));
                        const isGood = depth > 3;
                        const isWarning = depth > 1.6 && depth <= 3;
                        const isCritical = depth <= 1.6;
                        
                        return (
                          <div key={pos} className={`rounded-xl p-3 border-2 ${
                            isCritical ? 'bg-red-50 border-red-300' :
                            isWarning ? 'bg-amber-50 border-amber-300' :
                            'bg-emerald-50 border-emerald-300'
                          }`}>
                            <div className="text-center">
                              <div className={`text-2xl font-bold ${
                                isCritical ? 'text-red-700' : isWarning ? 'text-amber-700' : 'text-emerald-700'
                              }`}>{depth || '?'}mm</div>
                              <div className="text-xs text-slate-500 mt-1">{label}</div>
                              {/* Mini progress bar */}
                              <div className="h-1.5 bg-white/70 rounded-full overflow-hidden mt-2">
                                <div 
                                  className={`h-full rounded-full ${
                                    isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500'
                                  }`}
                                  style={{ width: `${lifePercent}%` }}
                                />
                              </div>
                              <div className={`text-xs font-semibold mt-1 ${
                                isCritical ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-emerald-600'
                              }`}>
                                {isCritical ? 'REPLACE' : isWarning ? 'MONITOR' : 'OK'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex justify-center mt-2">
                      <span className="text-xs text-slate-400 uppercase tracking-wider">Rear</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 px-2">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>🔴 &lt;1.6mm = Illegal</span>
                      <span>🟡 1.6-3mm = Low</span>
                      <span>🟢 &gt;3mm = Good</span>
                    </div>
                    <p className="text-xs text-slate-400 text-center mt-1">New tyres start at ~8mm</p>
                  </div>
                </div>
              )}
              
              {/* ========== BRAKE PADS ========== */}
              {data.health_indicators.brake_pads && (
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center gap-2 mb-4">
                    <Disc className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold text-slate-800">Brake Pad Condition</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Front Brakes', val: data.health_indicators.brake_pads.front_mm, subStatus: data.health_indicators.brake_pads.front_sub_status, healthPct: data.health_indicators.brake_pads.front_health_pct, healthLabel: data.health_indicators.brake_pads.front_health_label },
                      { label: 'Rear Brakes', val: data.health_indicators.brake_pads.rear_mm, subStatus: data.health_indicators.brake_pads.rear_sub_status, healthPct: data.health_indicators.brake_pads.rear_health_pct, healthLabel: data.health_indicators.brake_pads.rear_health_label }
                    ].filter(b => b.val).map(({ label, val, subStatus, healthPct, healthLabel }) => {
                      const thickness = parseFloat(val) || 0;
                      // Classification: ≤3=RED, >3-<4=REPLACE_SOON, ≥4-<6=MONITOR, ≥6=GREEN
                      const isRed = thickness <= 3.0;
                      const isReplaceSoon = thickness > 3.0 && thickness < 4.0;
                      const isMonitor = thickness >= 4.0 && thickness < 6.0;
                      const isGreen = thickness >= 6.0;
                      const lifePercent = healthPct != null ? healthPct : Math.min(100, Math.max(0, ((thickness - 1.5) / (12 - 1.5)) * 100));
                      const statusLabel = subStatus || (isRed ? 'Replace Now' : isReplaceSoon ? 'Replace Soon' : isMonitor ? 'Monitor' : 'Good');
                      const displayLabel = healthLabel || statusLabel;
                      
                      const bgClass = isRed ? 'bg-red-50 border-red-300' : (isReplaceSoon ? 'bg-amber-50 border-amber-400' : isMonitor ? 'bg-amber-50/60 border-amber-200' : 'bg-emerald-50 border-emerald-300');
                      const textClass = isRed ? 'text-red-700' : (isReplaceSoon || isMonitor) ? 'text-amber-700' : 'text-emerald-700';
                      const barClass = isRed ? 'bg-red-500' : (isReplaceSoon || isMonitor) ? 'bg-amber-500' : 'bg-emerald-500';
                      const labelClass = isRed ? 'text-red-600' : (isReplaceSoon || isMonitor) ? 'text-amber-600' : 'text-emerald-600';
                      
                      return (
                        <div key={label} className={`rounded-xl p-3 border-2 ${bgClass}`}>
                          <div className="text-center">
                            <div className={`text-2xl font-bold ${textClass}`}>{thickness}mm</div>
                            <div className="text-xs text-slate-500 mt-1">{label}</div>
                            {/* Progress bar showing life remaining */}
                            <div className="h-1.5 bg-white/70 rounded-full overflow-hidden mt-2">
                              <div 
                                className={`h-full rounded-full ${barClass}`}
                                style={{ width: `${lifePercent}%` }}
                              />
                            </div>
                            <div className={`text-xs font-semibold mt-1 ${labelClass}`}>
                              {displayLabel}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-3 px-2">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span className="text-red-500 font-medium">≤3mm Replace</span>
                      <span className="text-amber-500 font-medium">3-6mm Monitor</span>
                      <span className="text-emerald-500 font-medium">≥6mm Good</span>
                    </div>
                    <p className="text-xs text-slate-400 text-center mt-1">New pads start at ~12mm</p>
                  </div>
                </div>
              )}
              
              {/* ========== BRAKE DISCS ========== */}
              {data.health_indicators.brake_discs && (data.health_indicators.brake_discs.front_mm || data.health_indicators.brake_discs.rear_mm) && (
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center gap-2 mb-4">
                    <Circle className="w-5 h-5 text-slate-600" />
                    <span className="font-semibold text-slate-800">Brake Disc Thickness</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Front Discs', val: data.health_indicators.brake_discs.front_mm, minMm: data.health_indicators.brake_discs.front_min_mm || 22, newMm: 28 },
                      { label: 'Rear Discs', val: data.health_indicators.brake_discs.rear_mm, minMm: data.health_indicators.brake_discs.rear_min_mm || 9, newMm: 12 }
                    ].filter(b => b.val).map(({ label, val, minMm, newMm }) => {
                      const thickness = parseFloat(val) || 0;
                      const wearRange = newMm - minMm;
                      const remaining = thickness - minMm;
                      const lifePercent = Math.min(100, Math.max(0, (remaining / wearRange) * 100));
                      const isGood = lifePercent > 50;
                      const isWarning = lifePercent > 20 && lifePercent <= 50;
                      const isCritical = lifePercent <= 20;
                      
                      return (
                        <div key={label} className={`rounded-xl p-3 border-2 ${
                          isCritical ? 'bg-red-50 border-red-300' :
                          isWarning ? 'bg-amber-50 border-amber-300' :
                          'bg-emerald-50 border-emerald-300'
                        }`}>
                          <div className="text-center">
                            <div className={`text-2xl font-bold ${
                              isCritical ? 'text-red-700' : isWarning ? 'text-amber-700' : 'text-emerald-700'
                            }`}>{thickness}mm</div>
                            <div className="text-xs text-slate-500 mt-1">{label}</div>
                            {/* Progress bar showing life remaining */}
                            <div className="h-1.5 bg-white/70 rounded-full overflow-hidden mt-2">
                              <div 
                                className={`h-full rounded-full ${
                                  isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500'
                                }`}
                                style={{ width: `${lifePercent}%` }}
                              />
                            </div>
                            <div className={`text-xs font-semibold mt-1 ${
                              isCritical ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-emerald-600'
                            }`}>
                              {Math.round(lifePercent)}% life left
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-3 px-2">
                    <p className="text-xs text-slate-400 text-center">Discs wear down over time and must meet minimum thickness</p>
                  </div>
                </div>
              )}
              
              {/* ========== WIPERS (if available) ========== */}
              {data.health_indicators.wipers && (
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Droplets className="w-5 h-5 text-slate-600" />
                      <span className="font-semibold text-slate-800">Wiper Blades</span>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      data.health_indicators.wipers.status === 'good' ? 'bg-emerald-100 text-emerald-700' :
                      data.health_indicators.wipers.status === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {data.health_indicators.wipers.status === 'good' ? 'Good Condition' :
                       data.health_indicators.wipers.status === 'warning' ? 'Showing Wear' : 'Replace Recommended'}
                    </span>
                  </div>
                </div>
              )}
              
              {/* ========== LIGHTS (if issues found) ========== */}
              {data.health_indicators.lights && data.health_indicators.lights.issues?.length > 0 && (
                <div className="p-4 border-b border-slate-100 bg-amber-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-amber-600" />
                    <span className="font-semibold text-amber-800">Lighting Issues Found</span>
                  </div>
                  <ul className="space-y-1">
                    {data.health_indicators.lights.issues.map((issue, idx) => (
                      <li key={idx} className="text-sm text-amber-700 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        {issue.item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Green Items — iter197nn trust-first wording. */}
              {data.green_count > 0 && (
                <div className="p-4 bg-green-50" data-testid="portal-green-items-row">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <div className="text-left">
                      <span className="block text-sm font-semibold text-green-900 leading-tight">
                        Checked and Satisfactory
                      </span>
                      <span className="block text-[11.5px] text-green-700 leading-tight mt-0.5">
                        {data.green_count} {data.green_count === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============== BATTERY RADIO CODE WARNING ============== */}
      {allItems.some(item => item.label?.toLowerCase().includes('battery')) && (
        <div className="px-4 py-3">
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 mb-1">Radio Security Code Notice</h3>
                <p className="text-sm text-amber-800">
                  If battery replacement is carried out, your vehicle's radio may require a security code to be re-entered afterwards.
                </p>
                <p className="text-xs text-amber-700 mt-2">
                  Please ensure you have your radio code available. It's typically found in your owner's manual, on a card with your vehicle documents, or can be obtained from your dealer.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============== PRIMARY DECISION OPTIONS ============== */}
      <div className="px-4 py-5 space-y-3">
        <h2 className="font-semibold text-slate-900 mb-3">Choose an option:</h2>
        
        {/* Option 1: Complete All - Recommended */}
        <button
          onClick={() => handleSelectOption('all')}
          className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
            selectedOption === 'all'
              ? 'border-green-600 bg-green-100 ring-2 ring-green-500'
              : 'border-green-300 bg-green-50 hover:border-green-400 hover:bg-green-100'
          }`}
          data-testid="option-all"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {selectedOption === 'all' ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <Circle className="w-5 h-5 text-green-400" />
                )}
                <span className="font-semibold text-slate-900">Complete all recommended work</span>
              </div>
              <p className="text-sm text-slate-600 ml-7">
                Best for long-term reliability and avoids repeat visits
              </p>
            </div>
            <div className="text-right">
              <span className="font-bold text-slate-900" data-testid="option-all-price">£{grandTotal.toFixed(2)}</span>
              <span className="block text-xs text-green-600 font-medium">Recommended</span>
            </div>
          </div>
          {/* iter211n — sub-line breakdown mirrors the "If all
              recommended work is completed" block above so the £grandTotal
              here can be reconciled at a glance. */}
          {(chargesTotalIncVat > 0 || alreadyAuthorisedValue > 0) && (
            <p className="text-[11px] text-slate-500 mt-1.5 ml-7 leading-snug" data-testid="option-all-breakdown">
              £{allWorkTotal.toFixed(2)} work
              {chargesTotalIncVat > 0 && <> + £{chargesTotalIncVat.toFixed(2)} workshop charges</>}
              {alreadyAuthorisedValue > 0 && <> + £{alreadyAuthorisedValue.toFixed(2)} already booked</>}
            </p>
          )}
        </button>
        
        {/* Option 2: Essential Only — iter198p: only present when there
            are MOT-FAIL items (not just any red item). */}
        {essentialSafetyItems.length > 0 && (
          <button
            onClick={() => handleSelectOption('essential')}
            className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
              selectedOption === 'essential'
                ? 'border-red-600 bg-red-100 ring-2 ring-red-500'
                : 'border-red-300 bg-red-50 hover:border-red-400 hover:bg-red-100'
            }`}
            data-testid="option-essential"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {selectedOption === 'essential' ? (
                    <CheckCircle2 className="w-5 h-5 text-red-600" />
                  ) : (
                    <Circle className="w-5 h-5 text-red-400" />
                  )}
                  <span className="font-semibold text-slate-900">Essential safety work only</span>
                </div>
                <p className="text-sm text-slate-600 ml-7">
                  Addresses MOT failure items only — further work may be needed soon
                </p>
              </div>
              <div className="text-right">
                <span className="font-bold text-slate-900">£{(essentialSafetyTotal + alreadyAuthorisedValue).toFixed(2)}</span>
              </div>
            </div>
          </button>
        )}
        
        {/* Option 3: Budget */}
        <button
          onClick={() => handleSelectOption('budget')}
          className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
            selectedOption === 'budget'
              ? 'border-blue-600 bg-blue-100 ring-2 ring-blue-500'
              : 'border-blue-300 bg-blue-50 hover:border-blue-400 hover:bg-blue-100'
          }`}
          data-testid="option-budget"
        >
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {selectedOption === 'budget' ? (
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                ) : (
                  <Circle className="w-5 h-5 text-blue-400" />
                )}
                <span className="font-semibold text-slate-900">Help me choose for my budget</span>
              </div>
              <p className="text-sm text-slate-600 ml-7">
                We will prioritise the most important items to keep you safe
              </p>
            </div>
            <Wallet className="w-5 h-5 text-blue-500" />
          </div>
        </button>
        
        {/* Budget Input */}
        {showBudgetInput && (
          <div className="bg-white rounded-2xl border-2 border-slate-200 p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Enter your budget:
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">£</span>
                <input
                  type="number"
                  value={budgetAmount}
                  onChange={(e) => handleBudgetChange(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 border-2 border-slate-200 rounded-xl text-lg font-semibold focus:border-blue-500 focus:outline-none"
                  placeholder="0.00"
                  data-testid="budget-input"
                />
              </div>
            </div>
            
            {/* Budget Warning - No Hard Block */}
            {budgetWarning && !budgetProceedAnyway && (
              <div className="bg-amber-50 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">{budgetWarning.message}</p>
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Recommended minimum:</span>
                    <span className="font-semibold text-slate-900">£{budgetWarning.recommended.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Your budget:</span>
                    <span className="font-semibold text-slate-900">£{budgetWarning.selected.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => handleBudgetProceed(true)}
                    className="flex-1 py-2.5 px-4 bg-amber-600 text-white rounded-xl font-medium text-sm hover:bg-amber-700 transition-all"
                  >
                    Increase to £{budgetWarning.recommended.toFixed(2)}
                  </button>
                  <button
                    onClick={() => handleBudgetProceed(false)}
                    className="py-2.5 px-4 border-2 border-slate-200 rounded-xl font-medium text-sm text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Proceed anyway
                  </button>
                </div>
                <button
                  onClick={() => window.open(`tel:${data?.garage_phone || ''}`)}
                  className="w-full py-2.5 text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Talk to us
                </button>
              </div>
            )}
            
            {/* Budget Selection Preview */}
            {budgetAmount && (budgetProceedAnyway || !budgetWarning) && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">
                  Items included in your budget:
                </p>
                {getIncludedItems().map(item => (
                  <div key={item.id} className="flex items-center justify-between py-2 px-3 bg-green-50 rounded-lg">
                    <span className="text-sm text-green-800">{item.label}</span>
                    <span className="text-sm font-semibold text-green-900">£{(item.price_inc_vat || 0).toFixed(2)}</span>
                  </div>
                ))}
                {getIncludedItems().length === 0 && (
                  <p className="text-sm text-slate-500 italic">No items fit within this budget</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============== INCLUDED IN YOUR QUOTE (Consumables & Environmental) ============== */}
      {/* Moved here per FOH request — sits UNDER the pricing options so
          the customer first sees the work / totals, then the included
          materials & disposal handling explainer. Shows aggregated
          per-job-group ``charges[]`` + the yestimate-level global
          consumables / environmental disposal flags. */}
      {Array.isArray(data?.charges_summary) && data.charges_summary.length > 0 && (
        <div className="px-4 pt-4 pb-2" data-testid="portal-charges-summary">
          <div className="rounded-xl border-2 border-blue-200 bg-blue-50/70 p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                <Info className="w-4 h-4 text-blue-700" />
              </div>
              <div className="min-w-0">
                <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-blue-800 leading-tight">
                  Included in your quote
                </p>
                <p className="text-[13px] text-blue-900/90 mt-0.5 leading-snug">
                  Workshop materials and the responsible disposal of waste are
                  included as standard line items on the relevant jobs above.
                </p>
              </div>
            </div>
            <ul className="space-y-2.5" data-testid="portal-charges-list">
              {data.charges_summary.map((c, idx) => (
                <li
                  key={c.charge_type || idx}
                  className="bg-white rounded-lg border border-blue-100 px-3 py-2"
                  data-testid={`portal-charge-${c.charge_type || idx}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[13.5px] font-semibold text-slate-800">
                      {c.description}
                    </span>
                    <span className="text-[13.5px] font-bold text-slate-900 whitespace-nowrap">
                      £{Number(c.total || 0).toFixed(2)}
                    </span>
                  </div>
                  {c.explanation && (
                    <p className="text-[12px] text-slate-600 mt-1 leading-snug">
                      {c.explanation}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ============== SERVICE INTERVAL INTRO COPY ============== */}
      {data?.service_portal_meta?.has_service_items && (
        <div className="px-4 pt-4 pb-2" data-testid="service-portal-intro">
          <p className="text-sm text-slate-600 leading-relaxed">
            {data.service_portal_meta.service_intro_text}
          </p>
          {data.service_portal_meta.has_limited_history && data.service_portal_meta.limited_history_text && (
            <p className="text-sm text-slate-500 leading-relaxed mt-1 italic">
              {data.service_portal_meta.limited_history_text}
            </p>
          )}
        </div>
      )}


      {/* ============== TALK TO US BUTTON ============== */}
      <div className="px-4 py-4">
        <a
          href={`tel:${data?.garage_phone || ''}`}
          className={`w-full flex items-center justify-center gap-3 py-4 px-4 rounded-xl transition-all ${
            data?.garage_phone 
              ? 'bg-blue-50 hover:bg-blue-100 border border-blue-200' 
              : 'bg-slate-100 hover:bg-slate-200'
          }`}
          data-testid="talk-to-us"
        >
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
            <Phone className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <span className="font-semibold text-slate-800 block">
              {data?.garage_name || 'Talk to us'}
            </span>
            <span className="text-xs text-slate-500">
              {data?.garage_phone 
                ? `Tap to call ${data.garage_phone}` 
                : "Have questions? We're here to help"}
            </span>
          </div>
        </a>
      </div>

      {/* ============== CONFIRM SECTION ============== */}
      {/* iter199ak (Feb 2026) — Gate widened. Previously the section
          only appeared if the customer had at least one approved item
          OR a bulk option selected. That hid the Submit path entirely
          for legitimate "decline-only with reasons" flows AND for
          mixed flows where every per-tyre position got declined (so
          approvedItems was empty). We now also surface the section
          whenever any per-item decline has been recorded. */}
      {(approvedItems.size > 0
        || selectedOption
        || Object.keys(declinedItems || {}).length > 0
      ) && (
        <div
          ref={confirmSectionRef}
          className="px-4 py-5 border-t border-slate-200"
          data-testid="confirm-section"
        >
          <button
            onClick={() => {
              // iter211g — Was: toggle showConfirmSection. Clicking
              // the mid-page green "Confirm & Authorise" button
              // twice would collapse the section, so a customer who
              // clicked expecting to submit and then clicked again
              // would see the panel disappear. Now: always expand
              // the section AND scroll to keep the name/signature
              // inputs in view. The ACTUAL submit lives on the
              // "Authorise Work" button inside the expanded panel.
              setShowConfirmSection(true);
              setTimeout(() => {
                try {
                  // Scroll to the actual submit button so it's guaranteed
                  // visible above the fixed sticky bar.
                  const submitBtn = document.querySelector('[data-testid="submit-button"]');
                  if (submitBtn) {
                    submitBtn.scrollIntoView({ behavior: "smooth", block: "center" });
                  } else {
                    confirmSectionRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  }
                } catch (_) {/* no-op */}
              }, 220);
            }}
            className="w-full flex items-center justify-between py-3 px-4 bg-green-600 text-white rounded-xl font-semibold"
            data-testid="confirm-toggle"
          >
            <span>
              {approvedItems.size > 0 || selectedOption
                ? "Confirm & Authorise"
                : "Submit decisions to garage"}
            </span>
            <ArrowRight className="w-5 h-5" />
          </button>
          
          {showConfirmSection && (
            <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4 space-y-4">
              {/* Summary */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Work to be completed:</p>
                {getIncludedItems().map(item => (
                  <div key={item.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-700">{item.label}</span>
                    <span className="text-sm font-semibold">£{(item.price_inc_vat || 0).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 font-semibold">
                  <span>Total</span>
                  <span className="text-lg">£{selectedTotal.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Customer Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Your name:</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-green-500 focus:outline-none"
                  placeholder="Enter your full name"
                  data-testid="customer-name-input"
                />
              </div>
              
              {/* Signature */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sign to confirm:</label>
                <input
                  type="text"
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-green-500 focus:outline-none font-signature text-xl"
                  placeholder="Type your signature"
                  style={{ fontFamily: 'cursive' }}
                  data-testid="signature-input"
                />
              </div>
              
              {/* Reassurance */}
              <p className="text-xs text-slate-500 text-center">
                You are in control of what work is carried out.
              </p>
              
              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-lg transition-all disabled:opacity-50"
                data-testid="submit-button"
              >
                {submitting ? 'Submitting...' : 'Authorise Work'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ============== STICKY TOTAL BAR (ENHANCED PRICING CLARITY) ============== */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 shadow-lg z-40">
        {/* Three-column pricing summary. iter197c — charges no longer
            surfaced here either. "+ Additional" reflects the customer's
            selections (exclusive of workshop charges); charges live
            only in the dedicated charges-summary block above this bar
            (and are absent when FOH deselected them in pricing). The
            Total still includes everything. */}
        <div className="flex items-center justify-between text-sm" data-testid="portal-sticky-bar">
          <div className="text-center flex-1">
            <p className="text-xs text-slate-500">Already agreed</p>
            <p className="font-semibold text-slate-700">£{alreadyAuthorisedValue.toFixed(2)}</p>
          </div>
          <div className="text-center flex-1 border-l border-r border-slate-200 px-2">
            <p className="text-xs text-blue-600">+ Additional</p>
            <p className="font-semibold text-blue-700" data-testid="portal-sticky-additional">£{Math.max(0, selectedTotal - alreadyAuthorisedValue - (chargesTotalIncVat || 0)).toFixed(2)}</p>
          </div>
          <div className="text-center flex-1">
            <p className="text-xs text-slate-500">Total</p>
            <p className="font-bold text-lg text-slate-900">£{selectedTotal.toFixed(2)}</p>
          </div>
        </div>
        {getIncludedItems().length > 0 && (
          <p className="text-xs text-center text-green-600 font-medium mt-1">
            {getIncludedItems().length} {getIncludedItems().length === 1 ? 'item' : 'items'} selected
          </p>
        )}
        {/* iter199al (Feb 2026) — Decision summary preview. Surfaces a
            crisp two-tone tally of authorised vs declined items
            immediately above the sticky CTA so the customer can
            sanity-check their decisions before submitting. Reduces
            "I meant to approve that one" regret submissions and
            shifts the perceived control to the customer ahead of
            the confirm step. */}
        {(
          approvedItems.size > 0
          || selectedOption
          || Object.keys(declinedItems || {}).length > 0
        ) && !submitted && (() => {
          const includedCount = getIncludedItems().length;
          const includedValue = selectedTotal;
          const declinedCount = Object.keys(declinedItems || {}).length;
          const declinedValue = Object.keys(declinedItems || {}).reduce((s, id) => {
            const it = allItems.find((i) => i.id === id);
            return s + (it?.price_inc_vat || 0);
          }, 0);
          if (includedCount === 0 && declinedCount === 0) return null;
          return (
            <div
              className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] leading-tight"
              data-testid="portal-decision-summary"
            >
              <div className="flex items-center justify-between gap-3">
                {includedCount > 0 && (
                  <div className="flex items-center gap-1.5" data-testid="portal-decision-summary-approved">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-600 text-white text-[10px] leading-none">✓</span>
                    <span className="text-slate-700">
                      <span className="font-semibold text-slate-900">{includedCount}</span> authorised
                      <span className="text-slate-500"> · £{includedValue.toFixed(2)}</span>
                    </span>
                  </div>
                )}
                {declinedCount > 0 && (
                  <div className="flex items-center gap-1.5" data-testid="portal-decision-summary-declined">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] leading-none">✗</span>
                    <span className="text-slate-700">
                      <span className="font-semibold text-slate-900">{declinedCount}</span> declined
                      <span className="text-slate-500"> · £{declinedValue.toFixed(2)}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
        {/* iter199ak (Feb 2026) — Sticky "Continue → Confirm" CTA. The
            green Confirm & Authorise button lives mid-page and a
            customer scrolling through a long item list could easily
            scroll PAST it, especially after declining several
            amber-tier items with reasons. Surfacing the same CTA in
            the sticky bottom bar guarantees the next action is one
            tap away, no matter where the customer is on the page.
            Visible whenever ANY decision has been recorded
            (approved / selected option / declined). */}
        {(
          approvedItems.size > 0
          || selectedOption
          || Object.keys(declinedItems || {}).length > 0
        ) && !submitted && (
          <button
            type="button"
            onClick={() => {
              // iter211g — Robustified "Continue to Confirm" handler.
              // Previous version toggled the state, used a 50ms
              // timeout, and scrolled with block:"start" — which put
              // the confirm section top at viewport top, BUT the fixed
              // sticky bottom bar (200-220px) still covered the
              // signature input + Authorise Work button. Customers
              // clicked, saw the section header briefly, and reported
              // the button as "not working" because the ACTUAL submit
              // control was hidden behind the sticky bar.
              //
              // Fixes: always set state to true (not toggle) so
              // hitting the CTA twice never accidentally collapses;
              // increase the render-wait to 220ms so React commits
              // the expanded content before scroll fires; scroll
              // directly to the Authorise Work submit button (not
              // the section top) so the customer sees the ACTUAL
              // action at the bottom of the panel — the sticky
              // bar's height means scrolling to the section top
              // would put the submit button off-screen at the
              // bottom. Auto-focus the name input for immediate
              // visual feedback.
              setShowConfirmSection(true);
              setTimeout(() => {
                try {
                  // Prefer scrolling to the submit button so it is
                  // guaranteed visible above the sticky bottom bar.
                  const submitBtn = document.querySelector('[data-testid="submit-button"]');
                  if (submitBtn) {
                    submitBtn.scrollIntoView({ behavior: "smooth", block: "center" });
                  } else {
                    confirmSectionRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                } catch (_) {/* no-op */}
                // Auto-focus the name input for immediate feedback.
                try {
                  const nameInput = document.querySelector('[data-testid="customer-name-input"]');
                  if (nameInput && !nameInput.value) {
                    nameInput.focus({ preventScroll: true });
                  }
                } catch (_) {/* no-op */}
              }, 220);
            }}
            className="mt-3 w-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold rounded-lg py-3 text-sm shadow-md transition-colors flex items-center justify-center gap-2"
            data-testid="portal-sticky-confirm-cta"
          >
            <span>
              {approvedItems.size > 0 || selectedOption
                ? "Continue to Confirm"
                : "Submit decisions to garage"}
            </span>
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* ============== PHOTO MODAL — full-screen viewer with thumbnails ============== */}
      {enlargedPhoto && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex flex-col"
          onClick={closePhotoViewer}
          data-testid="portal-photo-viewer"
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-white">
              <Camera className="w-4 h-4" />
              <span className="text-sm font-bold uppercase tracking-wide">Tester Evidence</span>
              {enlargedSet.length > 1 && (
                <span className="text-xs text-white/70">
                  {Math.max(0, enlargedSet.indexOf(enlargedPhoto)) + 1} / {enlargedSet.length}
                </span>
              )}
            </div>
            <button
              type="button"
              className="text-white/90 hover:text-white p-2 -mr-2"
              onClick={closePhotoViewer}
              data-testid="portal-photo-viewer-close"
              aria-label="Close evidence viewer"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Main image — pinch-to-zoom on mobile is implicit via the
              touch viewport. Click anywhere outside the image closes. */}
          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
            <img
              src={enlargedPhoto}
              alt="Tester evidence"
              className="max-w-full max-h-full object-contain rounded-lg select-none"
              onClick={(e) => e.stopPropagation()}
              data-testid="portal-photo-viewer-image"
            />
          </div>

          {/* Thumbnail strip — only renders when there's more than one
              photo on the item. Tap a thumbnail to switch the active
              image without leaving the viewer. */}
          {enlargedSet.length > 1 && (
            <div
              className="flex gap-2 overflow-x-auto px-4 py-3 border-t border-white/10"
              onClick={(e) => e.stopPropagation()}
              data-testid="portal-photo-viewer-thumbs"
            >
              {enlargedSet.map((p, i) => {
                const active = p === enlargedPhoto;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setEnlargedPhoto(p)}
                    className={`flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${
                      active ? 'border-teal-400 scale-105' : 'border-white/20 opacity-70 hover:opacity-100'
                    }`}
                    data-testid={`portal-photo-viewer-thumb-${i}`}
                    aria-label={`View evidence photo ${i + 1}`}
                    aria-current={active}
                  >
                    <img src={p} alt={`Evidence ${i + 1}`} className="w-14 h-14 object-cover" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Caption */}
          <p className="text-xs text-white/60 text-center pb-3 px-4">
            Photo taken by the tester at the ramp · Tap outside or the × to close
          </p>
        </div>
      )}
    </div>
  );
};

export default MobileCustomerPortal;
